/**
 * Payroll Run Detail — v1 read-only + overlay editor (pt.80, 2026-09-16).
 *
 * Reachable from Payroll → Runs tab by clicking a row. Renders the compute
 * result of a Real or Sandbox run in a human-readable layout:
 *   • Header card (label, pay-week, sources, cross-links)
 *   • Upload dropzone for st_commission (sandbox only)
 *   • Compute button + overlay editor for invoice_balances
 *   • Per-tech paylines with expandable job_lines
 *   • Exceptions grouped by code, coloured by blocking severity
 *   • Pending Collection editor (writes back into overlay state)
 *   • Raw JSON <details> for trust/verification
 *
 * All numbers here come straight from the compute response — no client-side
 * recomputation. If a number looks wrong, the engine is authoritative.
 */
import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { DeliveryChip, DeliverySummary, SendTestEmailButton } from "@/components/payroll/TeamReviewDelivery";
import api from "@/lib/api";
import { paylineMoneyCells, jobLineMoneyCells, overageView, overageLineText, overageMismatchText, OVERAGE_LABEL, formulaLine, materialsLine, tierLabel, techSubLabel, showTfs as hasTfs, showMinWage } from "@/lib/payrollView";
import { MinWageSection } from "@/components/payroll/MinWageSection";
import { CSR_JOB_HDR, csrJobCells, CSR_NOT_COUNTED_HDR, csrNotCountedCells, money as fmtMoney } from "@/lib/payrollView";
import { CorrectionsPanel } from "@/components/payroll/CorrectionsPanel";
import { ArrowLeft, Upload, Play, ChevronRight, AlertCircle, FlaskConical,
  Coins, FileText, RefreshCw, ShieldCheck, Send, Users, MessageSquare, Download,
  Clock, CheckCircle2, XCircle, Lock, ClipboardPaste } from "lucide-react";
import { fmtPayWeek, derivePayrollState } from "./Payroll";
import { HeldJobsSection } from "@/components/payroll/HeldJobsSection";
import { PaidExternallyPanel } from "@/components/payroll/PaidExternallyPanel";
import { ST_REPORT_NAMES, ST_REPORT_CARDS, wrongFileMessage, fileMatchesReport, textMatchesReport, SLOT_HEADER_CODES } from "@/lib/stReportNames";

// CC-203: fixed instruction card shown above every ST input box, before anything is uploaded.
function ReportCard({ kind, frozen, required }) {
  const c = ST_REPORT_CARDS[kind];
  const paste = c.mode === "paste";
  const rows = [["Pull this report", ST_REPORT_NAMES[kind]], ["Date range", c.dateRange], ["How to get it in", c.how], ["Required or optional", required || c.required]];
  return (
    <div className="mb-3" data-testid={`payroll-report-card-${kind}`}>
      <div className="font-display font-semibold text-[14px] flex items-center gap-2 flex-wrap">
        {paste ? <ClipboardPaste size={14} strokeWidth={1.9} /> : <Upload size={14} strokeWidth={1.9} />}
        <span data-testid={`payroll-upload-label-${kind}`}>{ST_REPORT_NAMES[kind]}</span>
        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${paste ? "bg-[#5b3fa8] text-white" : "bg-[#0e1420] text-white"}`}
              data-testid={`payroll-report-mode-${kind}`}>{paste ? "PASTE TEXT" : "UPLOAD FILE"}</span>
        {frozen && <Lock size={13} className="text-[#8a5a1a]" />}
      </div>
      <div className="text-[12px] text-[color:var(--cc-ink-muted)] mt-0.5" data-testid={`payroll-report-subtitle-${kind}`}>{c.subtitle}</div>
      <ol className="mt-2 space-y-1 text-[11.5px] leading-snug list-none">
        {rows.map(([label, text], i) => (
          <li key={label} data-testid={`payroll-report-card-${kind}-line-${i + 1}`}>
            <span className="font-semibold text-[#0e1420]">{label}:</span> {text}
          </li>
        ))}
      </ol>
    </div>
  );
}

const BLOCKING_CODES = new Set([
  "HOURLY_TECH_PAY_NOT_YET_COMPUTED",
  // CC-065 (2026-10-XX): DEPARTED_TECH_HAS_REVENUE is no longer blocking —
  // revenue auto-routes into `house_account_bucket` per Brett's policy.
  // The exception still renders in the panel as an advisory so audit
  // reviewers can spot how much routed and to whom.
  "UNMATCHED_TECH",
  "PENDING_BALANCE_UNKNOWN",
  "SPLIT_JOB_CONFLICT",
]);

const money = (v) => (v == null ? "—"
  : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

// Signed money — positive is green ("owed to tech"), negative is red
// ("owed back from tech"). Zero is muted grey. Used exclusively by the
// read-only simulator delta column so the reviewer can eyeball who is
// owed money vs. who was overpaid at a glance.
const signedMoney = (v) => {
  const n = Number(v || 0);
  const s = `${n >= 0 ? "+" : "−"}$${Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
  return { text: n === 0 ? "—" : s, cls: n > 0 ? "text-[#0e8a68]" : n < 0 ? "text-[#7a1c1c]" : "text-[color:var(--cc-ink-muted)]" };
};

// CC-022c diagnostic (2026-11-24) — surfaces the "was the paste
// actually applied?" answer. A zero-delta result with an empty
// `simulated_split_overrides_applied` list and a non-empty
// `simulated_split_overrides_rejected` list means the compute engine
// silently rejected the paste — most commonly because the ST Invoice
// Item Detail export lists a single primary_technician per invoice,
// so `SplitJobError` never fires and the override path is never
// reached. Without this panel, "zero delta everywhere" is opaque.
function SimulatorSplitDiagnostic({ testId, result }) {
  const applied = Array.isArray(result?.simulated_split_overrides_applied)
    ? result.simulated_split_overrides_applied : [];
  const rejected = Array.isArray(result?.simulated_split_overrides_rejected)
    ? result.simulated_split_overrides_rejected : [];
  if (applied.length === 0 && rejected.length === 0) return null;
  const REASON_LABELS = {
    not_promoted_to_split:
      "Job has one primary_technician in the ST commission export AND no operator-decision promoted it to a split — the compute engine treats it as a solo job, so the paste has no attachment point on this run. If the job really is a split, promote it via 'Accept as Split' on the run's split-suggestions panel first.",
    tech_name_token_mismatch:
      "One or more techs on the promoted split list don't token-match any tech in the paste — override refuses to silently 0% a tech, falls back to hours-inference.",
    no_st_commission_rows_for_this_job:
      "This job number isn't in the ST commission upload — nothing to attribute against.",
    unknown: "Unknown rejection reason — see raw JSON.",
  };
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-lg overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 bg-[#fff8e6] border-b border-[#f0d78a] flex items-center gap-2">
        <AlertCircle size={14} className="text-[#8a5a00]" strokeWidth={2} />
        <div className="text-[12px] font-semibold text-[#8a5a00]">
          Attribution diagnostic — was the paste actually applied?
        </div>
      </div>
      <div className="px-3 py-2 text-[11.5px] text-[#0e1420] leading-relaxed border-b border-[#eef1f6]">
        <b>{applied.length}</b> job{applied.length === 1 ? "" : "s"} accepted the ST paste (source=<code>st_report</code>).{" "}
        <b>{rejected.length}</b> job{rejected.length === 1 ? "" : "s"} were silently rejected — those jobs used the same fallback as baseline (hours-inference or exclusion), which is why the delta looks like zero.
      </div>
      {applied.length > 0 && (
        <details open className="border-b border-[#eef1f6]">
          <summary className="cursor-pointer px-3 py-1.5 text-[11.5px] font-semibold bg-[#f3faf5] text-[#0e8a68] hover:bg-[#e6f4ea]">
            Accepted ({applied.length})
          </summary>
          <table className="w-full text-[11.5px] font-mono-data">
            <thead className="bg-[#fafbfd] text-left text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="px-3 py-1 font-semibold">Job #</th>
                <th className="px-3 py-1 font-semibold">Original total</th>
                <th className="px-3 py-1 font-semibold">Applied split</th>
              </tr>
            </thead>
            <tbody>
              {applied.map((a) => (
                <tr key={a.job_number} className="border-t border-[#eef1f6]">
                  <td className="px-3 py-1 align-top">{a.job_number}</td>
                  <td className="px-3 py-1 align-top">${Number(a.original_invoice_total || 0).toFixed(2)}</td>
                  <td className="px-3 py-1 align-top">
                    {(a.breakdown || []).map((b, i) => (
                      <div key={i}>{b.tech} — {(Number(b.share_pct || 0) * 100).toFixed(1)}%</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {rejected.length > 0 && (
        <details open>
          <summary className="cursor-pointer px-3 py-1.5 text-[11.5px] font-semibold bg-[#fbebeb] text-[#7a1c1c] hover:bg-[#f5dcdc]">
            Silently rejected ({rejected.length}) — pasted split had no effect
          </summary>
          <table className="w-full text-[11.5px] font-mono-data">
            <thead className="bg-[#fafbfd] text-left text-[10.5px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="px-3 py-1 font-semibold">Job #</th>
                <th className="px-3 py-1 font-semibold">Reason</th>
                <th className="px-3 py-1 font-semibold">ST commission primary_techs</th>
                <th className="px-3 py-1 font-semibold">Pasted techs</th>
              </tr>
            </thead>
            <tbody>
              {rejected.map((r) => (
                <tr key={r.job_number} className="border-t border-[#eef1f6]">
                  <td className="px-3 py-1 align-top">{r.job_number}</td>
                  <td className="px-3 py-1 align-top">
                    <div className="font-semibold text-[#7a1c1c]">{r.reason}</div>
                    <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] mt-0.5 leading-snug max-w-md font-sans">
                      {REASON_LABELS[r.reason] || ""}
                    </div>
                  </td>
                  <td className="px-3 py-1 align-top">
                    {(r.commission_primary_techs || []).map((t, i) => (
                      <div key={i}>
                        {t}
                        {(r.promoted_techs || []).length > 0 && !(r.promoted_techs || []).includes(t) && (
                          <span className="text-[10px] text-[color:var(--cc-ink-muted)] italic ml-1">(not promoted)</span>
                        )}
                      </div>
                    ))}
                    {(r.commission_primary_techs || []).length === 0 && <em className="text-[color:var(--cc-ink-muted)]">(none)</em>}
                    {(r.promoted_techs || []).length > (r.commission_primary_techs || []).length && (
                      <div className="mt-1 pt-1 border-t border-[#eef1f6] text-[10.5px] text-[color:var(--cc-ink-muted)] font-sans">
                        Promoted: {r.promoted_techs.join(", ")}
                        {r.operator_decision === "accept_as_split" && <span className="ml-1">(operator-decision)</span>}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-1 align-top">
                    {(r.pasted_techs || []).map((p, i) => (
                      <div key={i}>{p.tech_name} — {(Number(p.share_pct || 0) * 100).toFixed(1)}%</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </div>
  );
}

// CC-022c + CC-069b (2026-11-24) — presentation panel for a
// /simulate-* response payload. Both endpoints share the same shape:
//   { per_tech_diff: [{ tech, baseline_commission_gross,
//     simulated_commission_gross, delta_commission_gross }, ...],
//     total_commission_delta, note, ...meta }
// The table sorts by |delta| descending so the largest deltas surface
// first. Row 0 = biggest change (positive or negative).
function SimulatorResultPanel({ title, testId, result, kindLabel }) {
  const perTech = Array.isArray(result?.per_tech_diff) ? result.per_tech_diff : [];
  const sorted = [...perTech].sort(
    (a, b) => Math.abs(Number(b.delta_commission_gross || 0))
            - Math.abs(Number(a.delta_commission_gross || 0)),
  );
  const totalDelta = Number(result?.total_commission_delta || 0);
  const totalCls = totalDelta > 0 ? "text-[#0e8a68]" : totalDelta < 0 ? "text-[#7a1c1c]" : "text-[color:var(--cc-ink-muted)]";
  const changed = sorted.filter(r => Math.abs(Number(r.delta_commission_gross || 0)) >= 0.01);
  const unchanged = sorted.length - changed.length;
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-lg overflow-hidden" data-testid={testId}>
      <div className="px-3 py-2 bg-[#f3f5f8] border-b border-[#e7e9ee] flex items-center justify-between">
        <div className="font-display font-semibold text-[13px] flex items-center gap-2">
          {title}
          {result?.already_approved && (
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-[#0e8a68] text-white px-1.5 py-0.5 rounded" title="Simulated on an already-approved run. Nothing was persisted.">
              approved run · read-only
            </span>
          )}
        </div>
        <div className={`text-[12px] font-semibold ${totalCls}`}>
          net commission Δ:{" "}
          <span data-testid={`${testId}-total-delta`}>{signedMoney(totalDelta).text}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-[#fafbfd] text-left text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
            <tr>
              <th className="px-3 py-1.5 font-semibold">Technician</th>
              <th className="px-3 py-1.5 font-semibold text-right">Currently paid<br /><span className="text-[9.5px] normal-case tracking-normal font-normal">(baseline)</span></th>
              <th className="px-3 py-1.5 font-semibold text-right">Corrected<br /><span className="text-[9.5px] normal-case tracking-normal font-normal">(simulated)</span></th>
              <th className="px-3 py-1.5 font-semibold text-right">Δ ({kindLabel})</th>
            </tr>
          </thead>
          <tbody className="font-mono-data">
            {sorted.length === 0 && (
              <tr><td colSpan={4} className="px-3 py-3 text-center text-[color:var(--cc-ink-muted)] italic">No per-tech rows returned.</td></tr>
            )}
            {sorted.map((r) => {
              const delta = Number(r.delta_commission_gross || 0);
              const { text, cls } = signedMoney(delta);
              const rowCls = Math.abs(delta) >= 0.01 ? "bg-white" : "bg-[#fafbfd] text-[color:var(--cc-ink-muted)]";
              return (
                <tr key={r.person_id || r.tech} className={`border-t border-[#eef1f6] ${rowCls}`}>
                  <td className="px-3 py-1.5 font-sans">{r.tech || r.person_id || "—"}</td>
                  <td className="px-3 py-1.5 text-right">{money(r.baseline_commission_gross)}</td>
                  <td className="px-3 py-1.5 text-right">{money(r.simulated_commission_gross)}</td>
                  <td className={`px-3 py-1.5 text-right font-semibold ${cls}`}>{text}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {unchanged > 0 && (
        <div className="px-3 py-1.5 text-[10.5px] text-[color:var(--cc-ink-muted)] border-t border-[#eef1f6] bg-[#fafbfd]">
          {unchanged} tech{unchanged === 1 ? "" : "s"} unchanged (delta &lt; $0.01)
        </div>
      )}
      {result?.note && (
        <div className="px-3 py-2 text-[11px] text-[color:var(--cc-ink-muted)] border-t border-[#eef1f6] bg-white leading-snug">
          {result.note}
        </div>
      )}
      <details className="border-t border-[#eef1f6] bg-[#fafbfd]">
        <summary className="cursor-pointer px-3 py-1.5 text-[11px] text-[color:var(--cc-ink-muted)] font-semibold hover:text-[#0e1420]">
          Raw JSON
        </summary>
        <pre className="text-[10.5px] font-mono-data px-3 py-2 overflow-x-auto max-h-64" data-testid={`${testId}-raw-json`}>
          {JSON.stringify(result, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export default function PayrollRunDetail() {
  const { slug, runId } = useParams();
  const { pathname } = useLocation();
  const mode = pathname.includes("/test-runs/") ? "test" : "real";
  const base = mode === "test" ? "test-runs" : "runs";

  const [run, setRun] = useState(null);
  const [compute, setCompute] = useState(null);
  const [overlayBalances, setOverlayBalances] = useState({});
  // CC-368 — real runs: balances saved on the run; only boxes the editor changed are posted.
  const [savedBalances, setSavedBalances] = useState({});
  const [dirtyBalances, setDirtyBalances] = useState({});
  const [savedNote, setSavedNote] = useState(null);
  const hydratedRunRef = useRef(null);
  const [busy, setBusy] = useState(null); // 'upload' | 'compute' | 'load' | 'approve' | 'mark-sent' | 'send-for-approval' | 'send-for-team-review'
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [providerRef, setProviderRef] = useState("");
  // pt.93.4b — ack_no_ar_snapshot override modal state. Opens automatically
  // when /approve returns 409 NO_AR_SNAPSHOT_UPLOADED. Reason ≥ 20 chars
  // required; persisted to admin_audit_log server-side.
  const [ackModalOpen, setAckModalOpen] = useState(false);
  const [ackReason, setAckReason] = useState("");
  const [ackSubmitting, setAckSubmitting] = useState(false);
  // CC-068 — platform_admin approval-override modal.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideSubmitting, setOverrideSubmitting] = useState(false);
  const [approvalChecked, setApprovalChecked] = useState(false);
  // pt.87 — team review state (real runs only)
  const [teamReview, setTeamReview] = useState(null); // { reviews: [], can_send_for_approval, gate_reason, kind }
  // CC-070 (2026-09-25) — surface any /team-review/status failure inline
  // instead of silently setting teamReview=null. Prior behavior hid real
  // read errors behind the "no data yet" empty state.
  const [teamReviewLoadErr, setTeamReviewLoadErr] = useState(null);
  // CC-070 (2026-09-25) — inline per-tech send result surfaced under
  // the Team Review panel: { sent, already_sent, failed, excluded[],
  // dispatch_summary[], warning }.
  const [teamReviewSendResult, setTeamReviewSendResult] = useState(null);

  // CC-022b (paste input) — send pasted Payroll (Detail) text to the
  // paste endpoint. Same downstream shape as file upload.
  const onPasteSplits = async () => {
    const text = splitsPasteText.trim();
    if (!text) return;
    if (textMatchesReport("st_splits", text) === false) { setErr(wrongFileMessage("st_splits")); return; }  // CC-203
    setBusy("paste-st_splits"); setErr(null);
    try {
      const { data } = await api.post(
        `/payroll/runs/${runId}/paste/st_splits?tenant_slug=${slug}`,
        { text },
      );
      setSplitsPasteText("");
      await loadRun();
      await doCompute();
      if (data?.rows_count) {
        // Small nudge — a compute already re-runs so the Preflight will
        // update, but we surface the row-count so Brett sees paste
        // succeeded even if compute picks up nothing new.
        setErr(null);
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d?.message || d || e.message);
    } finally {
      setBusy(null);
    }
  };

  // CC-022c (2026-11-24) — simulate-splits: read-only, in-memory. Sends
  // the pasted text to /simulate-splits; renders per_tech_diff. Does not
  // touch persisted sources or the approved run.
  const onSimulateSplits = async () => {
    const text = simSplitsText.trim();
    if (!text) return;
    setBusy("sim-splits"); setSimErr(null);
    try {
      const { data } = await api.post(
        `/payroll/runs/${runId}/simulate-splits?tenant_slug=${slug}`,
        { text },
      );
      setSimSplitsResult(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSimErr(d?.message || (typeof d === "string" ? d : null) || e.message);
      setSimSplitsResult(null);
    } finally {
      setBusy(null);
    }
  };

  // CC-069b (2026-11-24) — simulate-job-costing: read-only, in-memory.
  // Uploads an ST Job Costing Summary and returns the delta each tech
  // would see if that file were applied. Does not persist.
  const onSimulateJobCosting = async (file) => {
    if (!file) return;
    setBusy("sim-job-costing"); setSimErr(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        `/payroll/runs/${runId}/simulate-job-costing?tenant_slug=${slug}`,
        form, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setSimJobCostingResult(data);
    } catch (e) {
      const d = e?.response?.data?.detail;
      setSimErr(d?.message || (typeof d === "string" ? d : null) || e.message);
      setSimJobCostingResult(null);
    } finally {
      setBusy(null);
    }
  };

  // CC-068 — override-approve dispatch.
  const doApproveOverride = async () => {
    const reason = overrideReason.trim();
    if (reason.length < 20) return;
    setOverrideSubmitting(true); setErr(null);
    try {
      await api.post(
        `/payroll/runs/${runId}/approve-override?tenant_slug=${slug}`,
        { reason },
      );
      setOverrideOpen(false);
      setOverrideReason("");
      await loadRun();
      await doCompute();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(d?.message || d || e.message);
    } finally {
      setOverrideSubmitting(false);
    }
  };

  const loadRun = async () => {
    setBusy("load"); setErr(null);
    try {
      const { data } = await api.get(`/payroll/${base}/${runId}?tenant_slug=${slug}`);
      setRun(data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };
  useEffect(() => { loadRun(); /* eslint-disable-next-line */ }, [slug, runId, base]);
  useEffect(() => {
    if (mode !== "real" || !run || hydratedRunRef.current === run.id) return;
    hydratedRunRef.current = run.id;
    const saved = run.invoice_balances_override || {};
    setSavedBalances(saved);
    setOverlayBalances(balancesToInputs(saved));
    setDirtyBalances({});
  }, [run, mode]);

  const balanceOverlays = () => {
    if (mode === "real") {
      const keys = Object.keys(dirtyBalances);
      return keys.length ? { invoice_balances: Object.fromEntries(keys.map((k) => {
        const v = overlayBalances[k];
        return [k, v === "" || v == null ? null : Number(v)];
      })) } : {};
    }
    return Object.keys(overlayBalances).length
      ? { invoice_balances: Object.fromEntries(
          Object.entries(overlayBalances)
            .filter(([, v]) => v !== "" && v != null)
            .map(([k, v]) => [k, Number(v)])
        ) }
      : {};
  };

  const doCompute = async () => {
    setBusy("compute"); setErr(null);
    try {
      const overlays = balanceOverlays();
      const { data } = await api.post(
        `/payroll/${base}/${runId}/compute?tenant_slug=${slug}`,
        { overlays },
      );
      setCompute(data);
      if (mode === "real" && data.invoice_balances_override) {
        setSavedBalances(data.invoice_balances_override);
        if (overlays.invoice_balances) {
          setOverlayBalances(balancesToInputs(data.invoice_balances_override));
          setSavedNote({ count: Object.keys(data.invoice_balances_override).length, at: data.computed_at });
        }
        setDirtyBalances({});
      }
    } catch (e) { setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  const fileInputRef = useRef();
  const hoursInputRef = useRef();
  const arInputRef = useRef();
  const splitsInputRef = useRef();  // CC-022b
  const simJobCostingInputRef = useRef();  // CC-069b + CC-022c simulator
  // CC-022b (2026-09-25 paste correction) — ST Payroll (Detail) Report
  // has no export button; paste is the primary input.
  const [splitsPasteText, setSplitsPasteText] = useState("");

  // CC-022c + CC-069b (2026-11-24) — read-only simulator state. All
  // three fields are pure in-memory; nothing persists. Splits simulator
  // consumes pasted text (flat CSV or stacked-block); materials
  // simulator consumes an uploaded ST Job Costing file. Both call
  // dedicated /simulate-* endpoints that never touch payroll_runs or
  // payroll_run_source_files. Result shape from both endpoints:
  //   { per_tech_diff: [{ tech, baseline_commission_gross,
  //     simulated_commission_gross, delta_commission_gross }],
  //     total_commission_delta, note, ... }
  const [simSplitsText, setSimSplitsText] = useState("");
  const [simSplitsResult, setSimSplitsResult] = useState(null);
  const [simJobCostingResult, setSimJobCostingResult] = useState(null);
  const [simErr, setSimErr] = useState(null);
  const onUpload = async (file, kind = "st_commission") => {
    if (!file) return;
    setErr(null);
    if ((await fileMatchesReport(kind, file)) === false) { setErr(wrongFileMessage(kind)); return; }  // CC-203
    setBusy("upload");
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(
        `/payroll/${base}/${runId}/upload/${kind}?tenant_slug=${slug}`,
        form, { headers: { "Content-Type": "multipart/form-data" } },
      );
      await loadRun();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(SLOT_HEADER_CODES.includes(d?.code) ? wrongFileMessage(kind) : (d || e.message));  // CC-203
    }
    finally { setBusy(null); }
  };

  // ---- pt.81 approval / mark-sent (real runs only) ----
  const doApprove = async () => {
    if (!approvalChecked) return;
    setBusy("approve"); setErr(null);
    try {
      await api.post(`/payroll/runs/${runId}/approve?tenant_slug=${slug}`, {});
      await loadRun();
      await doCompute(); // refresh compute so approved_by shows on-screen
      setApprovalChecked(false); // reset after successful approval
    } catch (e) {
      // pt.93.4b — detect the NO_AR_SNAPSHOT_UPLOADED 409 and open the
      // override modal instead of surfacing a raw error blob. The modal
      // requires a written reason (≥ 20 chars) that gets audit-logged.
      const detail = e?.response?.data?.detail;
      if (detail?.blocking_condition === "NO_AR_SNAPSHOT_UPLOADED") {
        setAckModalOpen(true);
        setAckReason("");
      } else {
        setErr(detail?.message || detail || e.message);
      }
    }
    finally { setBusy(null); }
  };

  // pt.93.4b — override approval path with audit-logged reason.
  const doApproveWithAck = async () => {
    const reason = ackReason.trim();
    if (reason.length < 20) return; // button disabled, guard anyway
    setAckSubmitting(true); setErr(null);
    try {
      const url = `/payroll/runs/${runId}/approve?tenant_slug=${slug}`
                + `&ack_no_ar_snapshot=true&reason=${encodeURIComponent(reason)}`;
      await api.post(url, {});
      setAckModalOpen(false);
      setAckReason("");
      setApprovalChecked(false);
      await loadRun();
      await doCompute();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(detail?.message || detail || e.message);
    } finally {
      setAckSubmitting(false);
    }
  };

  // ---- pt.87 team review (real runs only) ----
  // CC-070 (2026-09-25) — no longer swallows errors silently. Previously
  // any HTTP failure (500, 401, network) landed `setTeamReview(null)` with
  // NO in-app signal — that's how the prod symptom "panel shows 0 rows
  // despite 4 rows persisted in payroll_team_reviews" would hide behind a
  // "no data yet" empty state. The endpoint (`team_review_status`) has no
  // auth and just reads `payroll_team_reviews` — a failure here is a real
  // bug that operators need to see, not degrade gracefully around.
  const loadTeamReview = async () => {
    if (mode !== "real") return;
    try {
      const { data } = await api.get(`/payroll/runs/${runId}/team-review/status?tenant_slug=${slug}`);
      setTeamReview(data);
      setTeamReviewLoadErr(null);
    } catch (e) {
      setTeamReview(null);
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      setTeamReviewLoadErr(
        `Failed to load team review panel${status ? ` (HTTP ${status})` : ""}: ` +
        (typeof detail === "string" ? detail : (detail?.message || e.message))
      );
    }
  };
  useEffect(() => {
    if (mode === "real" && compute) loadTeamReview();
    // eslint-disable-next-line
  }, [mode, runId, compute]);

  // pt.90 — Download PDF export. Runs a fresh compute server-side (same
  // read path as the on-screen compute + approval digest — not a cached
  // collection) and streams the file back. Refuses on any blocking
  // exception count > 0 so a partial artifact never gets emailed to an
  // approver. Access-gated by canAccessPayroll allowlist on both sides.
  const doDownloadPdf = async () => {
    setBusy("download-pdf"); setErr(null);
    try {
      const res = await api.post(
        `/payroll/runs/${runId}/pdf?tenant_slug=${slug}`,
        {},
        { responseType: "blob" },
      );
      // Extract filename from Content-Disposition if present.
      let filename = `payroll-${slug}-${runId.slice(0, 8)}.pdf`;
      const cd = res.headers?.["content-disposition"] || res.headers?.get?.("content-disposition");
      if (cd) {
        const m = /filename="([^"]+)"/.exec(cd);
        if (m) filename = m[1];
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // Axios wraps blob-typed error responses; JSON body needs manual parse.
      let detail = e?.response?.data;
      if (detail instanceof Blob) {
        try {
          const txt = await detail.text();
          const parsed = JSON.parse(txt);
          detail = parsed?.detail?.message || parsed?.detail || txt;
        } catch { /* leave as-is */ }
      } else {
        detail = detail?.detail?.message || detail?.detail || e.message;
      }
      setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally { setBusy(null); }
  };

  // CC-070 (2026-09-25): the raw send response is surfaced inline on the
  // Team Review panel (per-tech `dispatch_summary[]` + `excluded[]`) so
  // failures per recipient are visible instead of being buried in a
  // window.alert. Prior version alerted a summary count only, which meant
  // that when 2 of 9 techs sent and 7 silently failed there was no in-app
  // signal about who or why.
  const doSendForTeamReview = async () => {
    setBusy("send-for-team-review"); setErr(null); setTeamReviewSendResult(null);
    try {
      const { data } = await api.post(
        `/payroll/runs/${runId}/team-review/send?tenant_slug=${slug}`, {},
      );
      setTeamReviewSendResult(data);
      if (data.all_mocked) {
        setErr(
          `Team review dispatch returned MOCKED results — no real emails went out. ` +
          (data.warning || "Check RESEND_API_KEY on the current deploy.")
        );
      }
      await loadTeamReview();
    } catch (e) {
      setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message);
    } finally { setBusy(null); }
  };

  // ---- pt.83 send-for-approval (real runs only) ----
  const doSendForApproval = async () => {
    setBusy("send-for-approval"); setErr(null);
    try {
      const { data } = await api.post(
        `/payroll/runs/${runId}/send-for-approval?tenant_slug=${slug}`, {},
      );
      // Immediate optimistic UX — surface the mock/real send result to the user.
      const mockNote = data?.email_result?.[`to_${(data.recipients || [])[0]}`]?.mocked
        ? " (preview: email mocked — real send fires only when autosend is on in prod)"
        : "";
      window.alert(
        `Notification fired to ${(data.recipients || []).join(", ")}` +
        (data.bcc?.length ? ` (BCC ${data.bcc.join(", ")})` : "") +
        mockNote,
      );
      await loadRun();
      await doCompute();
    } catch (e) { setErr(e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  const doMarkSent = async () => {
    setBusy("mark-sent"); setErr(null);
    try {
      await api.post(
        `/payroll/runs/${runId}/mark-sent?tenant_slug=${slug}`,
        { reference: providerRef.trim() || null },
      );
      await loadRun();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  if (!run) return (
    <div className="p-6 text-[13px] text-[color:var(--cc-ink-muted)]">
      {err ? <span className="text-[#7a1c1c]">{err}</span> : "Loading run…"}
    </div>
  );

  const sourcesUploaded = Object.keys(run.sources || {});
  const hasSTCommission = sourcesUploaded.includes("st_commission");
  const uploadedName = (kind) => (run.source_files || []).find((f) => f.kind === kind)?.filename || `${kind} on file`;  // CC-090
  const hasSTHours = sourcesUploaded.includes("st_hours");
  const hasSTAROpen = sourcesUploaded.includes("st_ar_open");
  const hasSTSplits = sourcesUploaded.includes("st_splits");  // CC-022b
  // CC-144 (2026-10-02) — frozen gate widened to all four upload cards
  // (previously CC-127 only). Reuses `derivePayrollState` from Payroll.jsx
  // so the Runs list column and the detail-page gate render from the
  // single source of truth. ARCHIVED is treated as frozen for all writes.
  const _runState = derivePayrollState(run);
  const _isRunFrozen = _runState.code !== "DRAFT";
  const _lockReasonLabel = {
    APPROVED:          "Run approved — all uploads and corrections are locked.",
    APPROVED_OVERRIDE: "Run approved via override (CC-068) — all uploads and corrections are locked.",
    PAID_OUTSIDE:      "This run was paid outside the platform — it is permanently locked.",
    ARCHIVED:          "This run is archived — restore it from the Runs list to make changes.",
  }[_runState.code] || "";
  // CC-068 — approval override badge state
  const isOverride = !!(compute?.approval_override ?? run.approval_override);
  const overrideReasonDisplay = compute?.approval_override_reason ?? run.approval_override_reason;
  const overrideBy = compute?.approval_override_by ?? run.approval_override_by;
  // CC-068 — server-derived: platform_admin OR PAYROLL_OVERRIDE_EMAILS. UX only — backend enforces.
  const canOverride = !!(compute?.viewer_can_override ?? run?.viewer_can_override);

  return (
    <div className="space-y-5" data-testid="payroll-run-detail">
      {/* ─────────── Header ─────────── */}
      <div>
        <Link
          to={`/${slug}/app/payroll`}
          data-testid="payroll-detail-back"
          className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-[color:var(--cc-ink-muted)] hover:text-[#0072bc]"
        >
          <ArrowLeft size={13} /> Back to Runs
        </Link>
        <div className="mt-2 flex items-start gap-3">
          {mode === "test" ? (
            <FlaskConical size={22} className="text-[#c96a1a] mt-1" />
          ) : (
            <Coins size={22} className="text-[#0072bc] mt-1" />
          )}
          <div>
            <div className="text-[12px] uppercase tracking-[.12em] text-[color:var(--cc-ink-muted)] font-semibold">
              {mode === "test" ? "Test / Sandbox Run" : "Real Payroll Run"} · Payroll
            </div>
            <h1
              className="font-display font-bold text-[22px] sm:text-[26px] tracking-tight text-[#0e1420] mt-1"
              data-testid="payroll-detail-label"
            >
              {run.label || `Pay week ${fmtPayWeek(run.pay_week_monday)}`}
            </h1>
            <div className="text-[13px] text-[color:var(--cc-ink-muted)] mt-0.5 font-mono-data">
              {fmtPayWeek(run.pay_week_monday)} → {fmtPayWeek(run.pay_week_sunday)}
              {(compute?.settings_snapshot?.rule_set || run.last_preview_rule_set) && (
                <span className="ml-2 px-1.5 py-0.5 rounded bg-[#eef1f6] text-[11px] font-mono-data" data-testid="payroll-detail-rule-set">
                  rule set {compute?.settings_snapshot?.rule_set || run.last_preview_rule_set}
                </span>
              )}
              {run.synthetic_anchor && <span className="ml-2 italic">(synthetic anchor)</span>}
              <span className="mx-2">·</span>
              status: <span className="font-semibold text-[#0e1420]">{run.status || "DRAFT"}</span>
              <span className="mx-2">·</span>
              sources: <span className="font-semibold text-[#0e1420]">{sourcesUploaded.join(", ") || "none"}</span>
            </div>
            {/* CC-131 (2026-10-02) — "Form changed since last Calculate" chip.
                Informational only; the CC-144 Patch A lock still gates writes
                on frozen / archived runs. Backend sets `form_material_newer_than_compute`
                via a cheap aggregation on GET /runs/{id}. */}
            {run.form_material_newer_than_compute && (
              <div
                className="mt-2 inline-flex items-center gap-1.5 bg-[#fef3d9] text-[#8a5a1a] px-2 py-1 rounded font-semibold text-[11.5px]"
                data-testid="payroll-detail-form-changed-since-compute"
                role="status" aria-live="polite"
              >
                Form 21330 edited since last Calculate — numbers may update when you re-run.
              </div>
            )}
            {mode === "real" && (
              <PaidExternallyPanel run={run} slug={slug} canAct={!!run.viewer_is_approver} onChanged={loadRun} />
            )}
            {mode === "real" && (
              <div className="mt-2 flex flex-wrap gap-2 items-center text-[11.5px]" data-testid="payroll-detail-lifecycle">
                {run.approved_by ? (
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded font-semibold ${
                      run.approval_override
                        ? "bg-[#fef3d9] text-[#8a5a1a]"
                        : "bg-[#e6f9f2] text-[#0e6b52]"
                    }`}
                    data-testid={run.approval_override ? "payroll-detail-override-badge" : "payroll-detail-approved-badge"}
                    title={run.approval_override ? `Override reason: ${run.approval_override_reason || "—"}` : ""}                  >
                    <ShieldCheck size={12} />
                    {run.approval_override ? (
                      <>OVERRIDE by {run.approval_override_by || run.approved_by} · {(run.approval_override_at || run.approved_at)?.slice(0,16).replace("T"," ")} · CC-068</>
                    ) : (
                      <>Approved by {run.approved_by} · {run.approved_at?.slice(0,16).replace("T"," ")}</>
                    )}
                  </span>
                ) : run.sent_for_approval_at ? (
                  <span className="inline-flex items-center gap-1 bg-[#e7f4fb] text-[#1c6a99] px-2 py-1 rounded font-semibold" data-testid="payroll-detail-sent-for-approval-badge">
                    <Send size={12} /> Sent for approval · {run.sent_for_approval_at?.slice(0,16).replace("T"," ")} · awaiting approver
                  </span>
                ) : run.approval_invalidated_at ? (
                  <span className="inline-flex items-center gap-1 bg-[#fef3d9] text-[#8a5a1a] px-2 py-1 rounded font-semibold" data-testid="payroll-detail-invalidated-badge">
                    Approval invalidated {run.approval_invalidated_at?.slice(0,16).replace("T"," ")} · re-approval required
                  </span>
                ) : run.send_for_approval_invalidated_at ? (
                  <span className="inline-flex items-center gap-1 bg-[#fef3d9] text-[#8a5a1a] px-2 py-1 rounded font-semibold" data-testid="payroll-detail-send-invalidated-badge">
                    Numbers changed after last send · re-send for approval required
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 bg-[#f5f6f8] text-[color:var(--cc-ink-muted)] px-2 py-1 rounded">
                    Not yet sent for approval
                  </span>
                )}
                {run.sent_to_provider_at && (
                  <span className="inline-flex items-center gap-1 bg-[#e7f4fb] text-[#1c6a99] px-2 py-1 rounded font-semibold" data-testid="payroll-detail-sent-badge">
                    <Send size={12} /> Sent to payroll provider · {run.sent_to_provider_at?.slice(0,16).replace("T"," ")}
                    {run.sent_to_provider_reference && <span className="ml-1 font-mono-data">· ref {run.sent_to_provider_reference}</span>}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {err && (
        <div className="flex items-start gap-3 bg-[#fdecec] border border-[#f7c1c1] rounded-xl p-3">
          <AlertCircle size={16} className="text-[#7a1c1c] mt-0.5" />
          <div className="text-[13px] text-[#7a1c1c]" data-testid="payroll-detail-err">
            {typeof err === "string" ? err : JSON.stringify(err)}
          </div>
        </div>
      )}

      {/* CC-067 v5: outside the compute-only block so it renders after a reload; real runs only. */}
      {mode === "real" && run && (
        <CorrectionsPanel runId={runId} slug={slug} frozen={_isRunFrozen}
                          canEdit={!!run.viewer_is_payroll_editor}
                          onRecompute={doCompute}
                          paylines={compute?.tech_paylines || run.last_preview_tech_paylines || []} />
      )}

      {/* CC-144 (2026-10-02) — Read-only banner on frozen / archived
          real runs. Rendered above the upload grid so operators see
          the lock reason before they try to drop a file. Simulator /
          test runs do not render this banner (mode==="real" gate). */}
      {_isRunFrozen && mode === "real" && (
        <div
          className="bg-[#fef7ec] border border-[#e6c99a] rounded-lg px-4 py-3 flex items-start gap-3"
          data-testid="payroll-detail-readonly-banner"
          role="status" aria-live="polite"
        >
          <Lock size={18} className="text-[#8a5a1a] mt-0.5" />
          <div className="text-[12.5px] text-[#5e3a0f] leading-snug">
            <div className="font-semibold" data-testid="payroll-detail-readonly-state-label">{_runState.label}</div>
            <div data-testid="payroll-detail-readonly-reason">{_lockReasonLabel}</div>
          </div>
        </div>
      )}

      {/* ─────────── Upload + Compute row ─────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid="payroll-detail-upload">
          <ReportCard kind="st_commission" frozen={_isRunFrozen} />
          {_isRunFrozen ? (
            <div
              className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center bg-[#fafbfd] opacity-70 cursor-not-allowed"
              data-testid="payroll-detail-commission-locked"
              aria-disabled="true"
            >
              <div className="text-[13px] font-semibold text-[#0e1420]">Uploads locked.</div>
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
                {_lockReasonLabel}
              </div>
              {hasSTCommission && (
                <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold" data-testid="payroll-upload-filename-st_commission-locked">✓ {uploadedName("st_commission")}</div>
              )}
            </div>
          ) : (
          <div
            className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center cursor-pointer hover:bg-[#f5f6f8]"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0]); }}
          >
            <div className="text-[13px] font-semibold text-[#0e1420]">
              {hasSTCommission ? "Replace uploaded file" : "Drop .xlsx or .csv"}
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
              or click to browse · max 25MB
            </div>
            <input
              ref={fileInputRef} type="file" accept=".xlsx,.csv"
              data-testid="payroll-detail-upload-input"
              onChange={(e) => onUpload(e.target.files?.[0])}
              className="hidden"
            />
            {busy === "upload" && <div className="text-[12px] text-[#0072bc] mt-2">Uploading + parsing…</div>}
            {hasSTCommission && !busy && (
              <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold break-all" data-testid="payroll-upload-filename-st_commission">
                ✓ {uploadedName("st_commission")}
              </div>
            )}
          </div>
          )}
        </div>

        <div className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid="payroll-detail-hours-upload">
          {/* CC-156: required for approval on pay weeks from 2026-10-12 (minimum-wage floor) */}
          <ReportCard kind="st_hours" frozen={_isRunFrozen}
                      required={(run.pay_week_monday || "") >= "2026-10-12" ? "Required to approve (minimum-wage floor)." : undefined} />
          {_isRunFrozen ? (
            <div
              className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center bg-[#fafbfd] opacity-70 cursor-not-allowed"
              data-testid="payroll-detail-hours-locked"
              aria-disabled="true"
            >
              <div className="text-[13px] font-semibold text-[#0e1420]">Uploads locked.</div>
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">{_lockReasonLabel}</div>
              {hasSTHours && (
                <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold" data-testid="payroll-upload-filename-st_hours-locked">✓ {uploadedName("st_hours")}</div>
              )}
            </div>
          ) : (
          <div
            className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center cursor-pointer hover:bg-[#f5f6f8]"
            onClick={() => hoursInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0], "st_hours"); }}
          >
            <div className="text-[13px] font-semibold text-[#0e1420]">
              {hasSTHours ? "Replace uploaded hours file" : "Drop .xlsx or .csv"}
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
              Unlocks SPLIT_JOB auto-attribution + hourly-tech pay
            </div>
            <input
              ref={hoursInputRef} type="file" accept=".xlsx,.csv"
              data-testid="payroll-detail-hours-upload-input"
              onChange={(e) => onUpload(e.target.files?.[0], "st_hours")}
              className="hidden"
            />
            {hasSTHours && !busy && (
              <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold break-all" data-testid="payroll-detail-hours-uploaded">
                ✓ {uploadedName("st_hours")}
              </div>
            )}
          </div>
          )}
        </div>

        <div className="bg-white border border-[#e7e9ee] rounded-xl p-4" data-testid="payroll-detail-ar-upload">
          <ReportCard kind="st_ar_open" frozen={_isRunFrozen} />
          {_isRunFrozen ? (
            <div
              className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center bg-[#fafbfd] opacity-70 cursor-not-allowed"
              data-testid="payroll-detail-ar-locked"
              aria-disabled="true"
            >
              <div className="text-[13px] font-semibold text-[#0e1420]">Uploads locked.</div>
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">{_lockReasonLabel}</div>
              {hasSTAROpen && (
                <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold" data-testid="payroll-upload-filename-st_ar_open-locked">✓ {uploadedName("st_ar_open")}</div>
              )}
            </div>
          ) : (
          <div
            className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center cursor-pointer hover:bg-[#f5f6f8]"
            onClick={() => arInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0], "st_ar_open"); }}
          >
            <div className="text-[13px] font-semibold text-[#0e1420]">
              {hasSTAROpen ? "Replace uploaded AR file" : "Drop .xlsx or .csv"}
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
              Auto-fills pending_collection balances. Pull ≥60 days trailing.
            </div>
            <input
              ref={arInputRef} type="file" accept=".xlsx,.csv"
              data-testid="payroll-detail-ar-upload-input"
              onChange={(e) => onUpload(e.target.files?.[0], "st_ar_open")}
              className="hidden"
            />
            {hasSTAROpen && !busy && (
              <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold break-all" data-testid="payroll-detail-ar-uploaded">
                ✓ {uploadedName("st_ar_open")}
              </div>
            )}
          </div>
          )}
        </div>

        <div className="bg-[#f7f4fd] border-2 border-[#cfc2ef] rounded-xl p-4" data-testid="payroll-detail-splits-upload">
          <ReportCard kind="st_splits" frozen={_isRunFrozen} />
          {_isRunFrozen ? (
            <div
              className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-6 text-center bg-[#fafbfd] opacity-70 cursor-not-allowed"
              data-testid="payroll-detail-splits-locked"
              aria-disabled="true"
            >
              <div className="text-[13px] font-semibold text-[#0e1420]">Paste and upload locked.</div>
              <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">{_lockReasonLabel}</div>
              {hasSTSplits && (
                <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold">✓ st_splits on file — ST-authoritative</div>
              )}
            </div>
          ) : (
          <>
          <textarea
            data-testid="payroll-detail-splits-paste-textarea"
            value={splitsPasteText}
            onChange={(e) => setSplitsPasteText(e.target.value)}
            rows={4}
            placeholder="Paste from ST browser view: 'Payroll Detail Report for [Name]' header, then Number / Invoiced On / Customer / Total / Split % / Subtotal / Cost / Bonus / Pay Adj. / NC Total / GP columns…"
            className="w-full border border-[#cfd4dc] rounded px-2 py-1.5 text-[11.5px] font-mono-data resize-y focus:outline-none focus:border-[#0072bc]"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10.5px] text-[color:var(--cc-ink-muted)]">
              {splitsPasteText.length ? `${splitsPasteText.length.toLocaleString()} chars` : "empty"}
            </span>
            <button
              type="button"
              onClick={onPasteSplits}
              disabled={!splitsPasteText.trim() || busy === "paste-st_splits"}
              data-testid="payroll-detail-splits-paste-submit-btn"
              className="text-[11.5px] font-semibold px-2 py-1 rounded bg-[#0072bc] text-white hover:bg-[#005f9c] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy === "paste-st_splits" ? "Parsing…" : "Parse pasted text"}
            </button>
          </div>
          <div
            className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-3 text-center cursor-pointer hover:bg-[#f5f6f8] mt-2"
            onClick={() => splitsInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
            onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files?.[0], "st_splits"); }}
          >
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)]">
              …or drop a .xlsx / .csv / .txt fallback file here
            </div>
            <input
              ref={splitsInputRef} type="file" accept=".xlsx,.csv,.txt"
              data-testid="payroll-detail-splits-upload-input"
              onChange={(e) => onUpload(e.target.files?.[0], "st_splits")}
              className="hidden"
            />
          </div>
          {hasSTSplits && !busy && (
            <div className="text-[11.5px] text-[#0e8a68] mt-2 font-semibold" data-testid="payroll-detail-splits-uploaded">
              ✓ st_splits on file — ST-authoritative
            </div>
          )}
          </>
          )}
        </div>

        <div className="bg-white border border-[#e7e9ee] rounded-xl p-4">
          <div className="font-display font-semibold text-[14px] mb-2 flex items-center gap-2">
            <Play size={14} strokeWidth={1.9} /> Compute (dry_run)
          </div>
          <button
            onClick={doCompute}
            disabled={busy === "compute" || !hasSTCommission}
            data-testid="payroll-detail-compute-btn"
            className="w-full bg-[#0072bc] text-white text-[13px] font-semibold px-3 py-2.5 rounded-lg hover:bg-[#005f9c] disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {busy === "compute" ? <><RefreshCw size={14} className="animate-spin" /> Computing…</> : <><Play size={14} /> Run compute</>}
          </button>
          <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-2">
            {mode === "test"
              ? "Sandbox run — dry_run hardwired to true, cannot persist."
              : "Real run — dry_run only. Persistence requires zero blocking exceptions (v2)."}
          </div>
          {compute && (
            <div className="mt-3 pt-3 border-t border-[#eef1f6] text-[12px] space-y-0.5 font-mono-data" data-testid="payroll-detail-compute-summary">
              <div>jobs_in_week: <span className="font-bold">{compute.totals.jobs_in_week}</span> · paylines: <span className="font-bold">{compute.tech_paylines.length}</span> · deferred: <span className="font-bold">{compute.totals.jobs_deferred_pending_collection}</span></div>
              <div>revenue: <span className="font-bold">{money(compute.totals.weekly_revenue_all_techs)}</span> · commission: <span className="font-bold">{money(compute.totals.final_commission_all_techs)}</span></div>
              <div>exceptions: <span className="font-bold">{compute.exceptions.length}</span> (<span className="text-[#7a1c1c]">{compute.exceptions.filter((e) => BLOCKING_CODES.has(e.code)).length} blocking</span>)</div>
              {compute.csr_sales_auto_match && (
                <CsrSalesAutoMatchLine info={compute.csr_sales_auto_match} slug={slug} />
              )}
            </div>
          )}
        </div>
      </div>

      {/* ─────────── Read-only simulators (CC-022c + CC-069b, 2026-11-24) ───────────
          Both endpoints are in-memory. They never write to payroll_runs,
          payroll_run_source_files, payroll_run_payouts, exceptions, or
          the audit log. Safe on an approved run — the run's approved
          numbers are untouched. Use to answer "if I applied this real
          data to the currently-approved run, what would each tech's
          commission look like?" without breaking the approval state. */}
      <div className="bg-[#f7f9fc] border border-[#cfd8e4] rounded-xl p-4 space-y-4" data-testid="payroll-detail-simulator-card">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-display font-semibold text-[14px] flex items-center gap-2">
              <FlaskConical size={14} strokeWidth={1.9} /> Read-only simulator
              <span className="text-[10.5px] uppercase tracking-wider text-white font-semibold bg-[#0e1420] px-1.5 py-0.5 rounded">
                CC-022c + CC-069b
              </span>
            </div>
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1 leading-relaxed max-w-2xl">
              Simulates the impact of splits or material-cost data <b>without persisting anything</b>. The run's approval state, source files, and payouts are not touched. Use this to preview "approved vs. corrected" numbers before deciding whether to unlock and re-approve.
            </div>
          </div>
          {(simSplitsResult || simJobCostingResult) && (
            <button
              type="button"
              onClick={() => { setSimSplitsResult(null); setSimJobCostingResult(null); setSimErr(null); }}
              data-testid="payroll-detail-simulator-clear-btn"
              className="text-[11px] font-semibold text-[#7a1c1c] hover:underline whitespace-nowrap"
            >
              Clear results
            </button>
          )}
        </div>

        {simErr && (
          <div className="bg-white border border-[#e6b3b3] text-[#7a1c1c] rounded-lg px-3 py-2 text-[12px]" data-testid="payroll-detail-simulator-error">
            {String(simErr)}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ─── Splits simulator ─── */}
          <div className="bg-white border border-[#e7e9ee] rounded-lg p-3">
            <div className="font-display font-semibold text-[13px] mb-1 flex items-center gap-2">
              <FlaskConical size={13} strokeWidth={1.9} /> Simulate splits (CC-022c)
            </div>
            <div className="text-[11px] text-[color:var(--cc-ink-muted)] mb-2 leading-snug">
              Paste the ST Payroll (Detail) Report — stacked blocks or flat CSV both work. Preview the per-tech delta before deciding to save.
            </div>
            <textarea
              data-testid="payroll-detail-simulator-splits-textarea"
              value={simSplitsText}
              onChange={(e) => setSimSplitsText(e.target.value)}
              rows={4}
              placeholder="Paste ST Payroll Detail text (or flat CSV: Technician,Number,Invoiced On,…,Split %,…)"
              className="w-full border border-[#cfd4dc] rounded px-2 py-1.5 text-[11.5px] font-mono-data resize-y focus:outline-none focus:border-[#0072bc]"
            />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10.5px] text-[color:var(--cc-ink-muted)]">
                {simSplitsText.length ? `${simSplitsText.length.toLocaleString()} chars` : "empty"}
              </span>
              <button
                type="button"
                onClick={onSimulateSplits}
                disabled={!simSplitsText.trim() || busy === "sim-splits"}
                data-testid="payroll-detail-simulator-splits-run-btn"
                className="text-[11.5px] font-semibold px-2 py-1 rounded bg-[#0e1420] text-white hover:bg-[#22283a] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busy === "sim-splits" ? "Simulating…" : "Run splits simulation"}
              </button>
            </div>
          </div>

          {/* ─── Materials simulator ─── */}
          <div className="bg-white border border-[#e7e9ee] rounded-lg p-3">
            <div className="font-display font-semibold text-[13px] mb-1 flex items-center gap-2">
              <FlaskConical size={13} strokeWidth={1.9} /> Simulate materials (CC-069b)
            </div>
            <div className="text-[11px] text-[color:var(--cc-ink-muted)] mb-2 leading-snug">
              Upload an ST Job Costing Summary. Preview each tech's commission delta from the real material-cost overage before applying it.
            </div>
            <div
              className="border-2 border-dashed border-[#cfd4dc] rounded-lg p-4 text-center cursor-pointer hover:bg-[#f5f6f8]"
              onClick={() => simJobCostingInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
              onDrop={(e) => { e.preventDefault(); onSimulateJobCosting(e.dataTransfer.files?.[0]); }}
              data-testid="payroll-detail-simulator-materials-dropzone"
            >
              <div className="text-[12px] font-semibold text-[#0e1420]">
                {busy === "sim-job-costing" ? "Simulating…" : "Drop .xlsx or .csv"}
              </div>
              <div className="text-[11px] text-[color:var(--cc-ink-muted)] mt-0.5">
                Not persisted. Read-only.
              </div>
              <input
                ref={simJobCostingInputRef} type="file" accept=".xlsx,.csv"
                data-testid="payroll-detail-simulator-materials-input"
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onSimulateJobCosting(f); }}
                className="hidden"
              />
            </div>
          </div>
        </div>

        {/* ─── Splits result table ─── */}
        {simSplitsResult && (
          <>
            <SimulatorResultPanel
              title="Splits simulation result"
              testId="payroll-detail-simulator-splits-result"
              result={simSplitsResult}
              kindLabel="splits"
            />
            <SimulatorSplitDiagnostic
              testId="payroll-detail-simulator-splits-diagnostic"
              result={simSplitsResult}
            />
          </>
        )}

        {/* ─── Materials result table ─── */}
        {simJobCostingResult && (
          <SimulatorResultPanel
            title="Materials simulation result"
            testId="payroll-detail-simulator-materials-result"
            result={simJobCostingResult}
            kindLabel="material costs"
          />
        )}
      </div>

      {/* ─────────── Compute results ─────────── */}
      {compute && (
        <>
          {/* pt.81 + pt.83 — real-run send-for-approval + approval + provider-sent controls
              pt.90 — Download PDF button lives in the same bar, disabled with the same gates. */}
          {mode === "real" && (
            <div className="bg-white border border-[#e7e9ee] rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="payroll-detail-approval-bar">
              {(() => {
                const blockers = compute.blocking_exception_count || 0;
                const disabled = blockers > 0 || busy === "download-pdf";
                let tooltip;
                if (blockers > 0) {
                  tooltip = `Resolve ${blockers} blocking exception${blockers === 1 ? "" : "s"} first — a partial PDF would mislead the approver.`;
                } else if (busy === "download-pdf") {
                  tooltip = "Generating…";
                } else {
                  tooltip = "Generate a printable/emailable PDF from the current compute. Same data the on-screen table shows.";
                }
                return (
                  <button
                    onClick={doDownloadPdf}
                    disabled={disabled}
                    title={tooltip}
                    data-testid="payroll-detail-download-pdf-btn"
                    className="bg-[#0e1420] text-white text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#22283a] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Download size={14} /> {busy === "download-pdf" ? "Building PDF…" : "Download PDF"}
                  </button>
                );
              })()}
              {/* Send-for-Approval — editor triggers the notification.
                  Visible whenever there is no current 'sent_for_approval_at' and no approval. */}
              {!compute.approved_by && !compute.sent_for_approval_at && (() => {
                const blockers = compute.blocking_exception_count || 0;
                const teamReviewOpen = teamReview && (teamReview.reviews || []).length > 0 && !teamReview.can_send_for_approval;
                const disabled = blockers > 0 || teamReviewOpen || busy === "send-for-approval";
                let tooltip;
                if (blockers > 0) {
                  tooltip = `${blockers} blocking exception${blockers === 1 ? "" : "s"} must be resolved before this run can be sent for approval.`;
                } else if (teamReviewOpen) {
                  tooltip = teamReview.gate_reason || "Team review still in progress — waiting on responses or 4h timeout.";
                } else {
                  tooltip = "Fires an email notification to the approver on PAYROLL_APPROVER_EMAILS with a direct link to this run.";
                }
                return (
                  <button
                    onClick={doSendForApproval}
                    disabled={disabled}
                    title={tooltip}
                    data-testid="payroll-detail-send-for-approval-btn"
                    className="bg-[#0072bc] text-white text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#005f9c] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Send size={14} /> {busy === "send-for-approval" ? "Sending…" : "Send for approval"}
                    {blockers > 0 && (
                      <span data-testid="payroll-detail-send-blockers-count" className="ml-1 bg-white text-[#7a1c1c] text-[10px] font-bold px-1.5 py-0.5 rounded">
                        {blockers} blocker{blockers === 1 ? "" : "s"}
                      </span>
                    )}
                    {teamReviewOpen && blockers === 0 && (
                      <span data-testid="payroll-detail-team-review-gate-badge" className="ml-1 bg-white text-[#8a5a1a] text-[10px] font-bold px-1.5 py-0.5 rounded">
                        team review open
                      </span>
                    )}
                  </button>
                );
              })()}

              {/* CC-158 (2026-10-05) — clear banner for signed-in non-approvers.
                  Prior UI (removed below) was a cryptic status line
                  ending in a bare `PAYROLL_APPROVER_EMAILS` env name,
                  which left users like Nathanael staring at a run page
                  with no actionable path to approve. New: explicit
                  banner identifying the viewer as not-on-allowlist,
                  with a direct ask ("ask Brett to add you") so the
                  path forward is unambiguous. Keeps the original
                  data-testid so existing selectors still resolve. */}
              {!compute.approved_by && compute.sent_for_approval_at && !compute.viewer_is_approver && (
                <div
                  data-testid="payroll-detail-awaiting-approval"
                  className="w-full bg-[#fff8e1] border border-[#f0d678] rounded-lg px-3 py-2.5 flex items-start gap-2.5"
                >
                  <Send size={14} className="text-[#8a6a1a] mt-0.5 shrink-0" />
                  <div className="text-[12.5px] text-[#5c4612] leading-snug">
                    <div className="font-semibold">
                      Sent for approval by {compute.sent_for_approval_by}.
                    </div>
                    <div className="mt-0.5">
                      <b>You are not currently on the approver allowlist</b>, so
                      the Approve button is hidden for your account. Ask Brett
                      to add your email to <code className="font-mono-data text-[11.5px]">PAYROLL_APPROVER_EMAILS</code>{" "}
                      if you should be able to approve payroll runs. Approvers
                      currently on the list see an "Approve this run" button in
                      this same spot.
                    </div>
                  </div>
                </div>
              )}

              {/* Approver's screen — confirmation checkbox gates the Approve button. */}
              {compute.viewer_is_approver && !compute.approved_by && compute.sent_for_approval_at && (
                <div className="flex flex-wrap items-center gap-3 w-full">
                  <label className="flex items-start gap-2 cursor-pointer select-none text-[12.5px] text-[#0e1420] max-w-[540px]">
                    <input
                      type="checkbox"
                      data-testid="payroll-detail-approval-confirm-checkbox"
                      checked={approvalChecked}
                      onChange={(e) => setApprovalChecked(e.target.checked)}
                      className="accent-[#0e8a68] mt-0.5"
                    />
                    <span>
                      <b>I have reviewed these numbers and approve this payroll run.</b>
                      <span className="block text-[11.5px] text-[color:var(--cc-ink-muted)] mt-0.5">
                        Approval is bound to the current compute digest.
                        If numbers change afterwards, this approval auto-invalidates and the
                        editor must re-send for approval.
                      </span>
                    </span>
                  </label>
                  <button
                    onClick={doApprove}
                    disabled={!approvalChecked || busy === "approve"}
                    data-testid="payroll-detail-approve-btn"
                    className="bg-[#0e8a68] text-white text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#0a6f53] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <ShieldCheck size={14} /> {busy === "approve" ? "Approving…" : "Approve this run"}
                  </button>
                </div>
              )}

              {/* Approver's screen — nothing yet sent → let them know. */}
              {compute.viewer_is_approver && !compute.approved_by && !compute.sent_for_approval_at && (
                <span data-testid="payroll-detail-approver-awaiting-send" className="text-[12.5px] text-[color:var(--cc-ink-muted)]">
                  Waiting on the payroll editor to click <b>Send for approval</b>. You will get an email once they do.
                </span>
              )}

              {/* Approved state — visible to everyone. CC-068 renders a
                   distinct amber badge for platform_admin overrides so
                   nobody confuses an override with a real approver click. */}
              {compute.approved_by && (
                <span
                  data-testid={isOverride ? "payroll-detail-override-inline" : "payroll-detail-approved-inline"}
                  className={`text-[12.5px] font-semibold flex items-center gap-1 ${isOverride ? "text-[#8a5a1a]" : "text-[#0e6b52]"}`}
                  title={isOverride ? `Override reason: ${overrideReasonDisplay || "—"}` : ""}
                >
                  <ShieldCheck size={12} />
                  {isOverride ? (
                    <>OVERRIDE by {overrideBy || compute.approved_by} — not a real approver click (CC-068)</>
                  ) : (
                    <>Approved by {compute.approved_by} · numbers locked to current compute</>
                  )}
                </span>
              )}
              {/* CC-068 — platform-admin override entry point.
                   Only shown when run is unapproved and viewer_can_override. */}
              {canOverride && !compute.approved_by && (
                <button
                  type="button"
                  onClick={() => { setOverrideOpen(true); setOverrideReason(""); }}
                  data-testid="payroll-detail-override-open-btn"
                  className="text-[11.5px] font-semibold text-[#8a5a1a] hover:underline inline-flex items-center gap-1 border border-[#f0d999] bg-[#fef3d9] px-2 py-1 rounded"
                  title="Platform-admin approval override (CC-068). Use ONLY when this run was approved out-of-band (e.g. PDF review email) and needs closing in the app."
                >
                  ⚠ Approve via override
                </button>
              )}

              {/* Persist button removed here — status transitions to COMPUTED via existing dry_run=false compute path.
                  Once persisted, mark-sent-to-provider appears below. */}
              {(run.status === "COMPUTED" || run.status === "APPROVED") && !run.sent_to_provider_at && (
                <div className="ml-auto flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Provider batch/ref (optional)"
                    value={providerRef}
                    onChange={(e) => setProviderRef(e.target.value)}
                    data-testid="payroll-detail-provider-ref-input"
                    className="border border-[#cfd4dc] rounded px-2 py-1.5 text-[12.5px] w-[220px]"
                  />
                  <button
                    onClick={doMarkSent}
                    disabled={busy === "mark-sent"}
                    data-testid="payroll-detail-mark-sent-btn"
                    className="bg-[#1c6a99] text-white text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#155073] disabled:opacity-50 flex items-center gap-2"
                  >
                    <Send size={14} /> {busy === "mark-sent" ? "Marking…" : "Mark as sent to payroll provider"}
                  </button>
                </div>
              )}
            </div>
          )}
          <PaylinesTable paylines={compute.tech_paylines} expanded={expanded} setExpanded={setExpanded} runId={runId} slug={slug} mode={mode} onJobToggled={doCompute} />
          {mode === "real" && (
            <TeamReviewSection
              teamReview={teamReview}
              loadErr={teamReviewLoadErr}
              sendResult={teamReviewSendResult}
              onDismissSendResult={() => setTeamReviewSendResult(null)}
              blockingCount={compute.blocking_exception_count || 0}
              alreadySentForApproval={!!compute.sent_for_approval_at}
              alreadyApproved={!!compute.approved_by}
              busy={busy}
              onSend={doSendForTeamReview}
              onRefresh={loadTeamReview}
              runId={runId}
              slug={slug}
              canEdit={!!run?.viewer_is_payroll_editor}
            />
          )}
          {(compute.hours_suggest_splits || []).length > 0 && (
            <HoursSuggestSplitPanel
              runId={runId}
              slug={slug}
              mode={mode}
              suggestions={compute.hours_suggest_splits}
              onDecision={doCompute}
            />
          )}
          {(compute.hours_split_decisions_applied || []).length > 0 && (
            <div className="bg-[#f0f8ff] border border-[#c2dff0] rounded-xl p-4 text-[12.5px]" data-testid="payroll-detail-decisions-applied">
              <div className="font-display font-semibold text-[13.5px] text-[#1c6a99] mb-1.5">
                Persisted split decisions applied to this compute
              </div>
              <ul className="ml-4 list-disc space-y-1">
                {(compute.hours_split_decisions_applied || []).map((d) => (
                  <li key={d.job_number}>
                    Job <code className="font-mono-data">{d.job_number}</code> ·
                    <b className="ml-1">{d.decision.replace(/_/g, " ")}</b> ·
                    by {d.decided_by} · {d.decided_at?.slice(0, 16).replace("T", " ")}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <ExceptionsPanel exceptions={compute.exceptions} />
          <FormReviewSection rows={compute.form_review_jobs} />
          <SplitPreflightSection
            attributed={compute.split_attributed}
            unresolved={compute.split_jobs}
            hasStSplits={hasSTSplits}
          />
          <HouseAccountSection bucket={compute.house_account_bucket} />
          <CsrCommissionSection csr={compute.csr_combined_line} card={compute.csr_card || run?.last_preview_csr_card} />
          <PendingCollectionEditor
            pending={compute.pending_collection}
            overlayBalances={overlayBalances}
            setOverlayBalances={setOverlayBalances}
            onRecompute={doCompute}
            busy={busy === "compute"}
            persisted={mode === "real"}
            saved={savedBalances}
            dirty={dirtyBalances}
            setDirty={setDirtyBalances}
            savedNote={savedNote}
            frozen={!!compute.frozen}
          />
          {mode === "real" && (
            <HeldJobsSection carryover={compute.carryover} runId={runId} slug={slug}
                             frozen={!!compute.frozen} onChanged={doCompute} />
          )}
          {mode === "real" && <MinWageSection mw={compute.min_wage} />}
          <details className="bg-white border border-[#e7e9ee] rounded-xl">
            <summary className="cursor-pointer px-5 py-3 text-[13px] font-semibold text-[color:var(--cc-ink-muted)] flex items-center gap-2" data-testid="payroll-detail-raw-json-toggle">
              <FileText size={13} /> Raw compute JSON (for verification)
            </summary>
            <pre className="px-5 py-3 text-[11px] font-mono-data overflow-auto max-h-[400px] border-t border-[#eef1f6] bg-[#f9fafb]" data-testid="payroll-detail-raw-json">
              {JSON.stringify(compute, null, 2)}
            </pre>
          </details>
        </>
      )}

      {/* pt.93.4b — AR-snapshot override modal. Renders only when /approve
          returned 409 NO_AR_SNAPSHOT_UPLOADED. Reason ≥ 20 chars required,
          audit-logged server-side to admin_audit_log. */}
      {ackModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          data-testid="payroll-detail-ack-no-ar-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-[560px] w-[92vw] p-6 border-2 border-[#a01d1d]">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle size={22} className="text-[#a01d1d] shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-[15px] text-[#a01d1d]">
                  No AR snapshot uploaded — override required
                </div>
                <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1 leading-relaxed">
                  This run has no <code className="bg-[#f4f4f4] px-1 rounded">st_ar_open</code> CSV.
                  Approving without it means CSR sales on this run cannot auto-promote from
                  <b> Stage 5 (needs_pay_to_collect) → Stage 6 (completed_collected)</b>.
                  The preferred fix is to upload the AR CSV. Only override if this is a
                  first-ever payroll, a migration window, or an urgent hotfix.
                </div>
              </div>
            </div>
            <label className="block text-[12px] font-semibold text-[#0e1420] mb-1">
              Written justification (audit-logged, ≥ 20 characters)
            </label>
            <textarea
              data-testid="payroll-detail-ack-reason-textarea"
              value={ackReason}
              onChange={(e) => setAckReason(e.target.value)}
              rows={3}
              placeholder="e.g. First payroll for tenant, AR baseline not yet established. Katt informed 2026-09-22."
              className="w-full border border-[#cfd4dc] rounded px-3 py-2 text-[13px] font-mono-data resize-none focus:outline-none focus:border-[#a01d1d]"
            />
            <div className="flex items-center justify-between mt-1 text-[11px]">
              <span className={ackReason.trim().length < 20 ? "text-[#a01d1d]" : "text-[#0e6b52]"}>
                {ackReason.trim().length} / 20 chars minimum
              </span>
              <span className="text-[color:var(--cc-ink-muted)]">
                Persisted to admin_audit_log + payroll_runs
              </span>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                data-testid="payroll-detail-ack-cancel-btn"
                onClick={() => { setAckModalOpen(false); setAckReason(""); }}
                disabled={ackSubmitting}
                className="border border-[#cfd4dc] text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#f4f4f4] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                data-testid="payroll-detail-ack-confirm-btn"
                onClick={doApproveWithAck}
                disabled={ackReason.trim().length < 20 || ackSubmitting}
                className="bg-[#a01d1d] text-white text-[13px] font-semibold px-3 py-2 rounded-lg hover:bg-[#7a1616] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ShieldCheck size={14} />
                {ackSubmitting ? "Approving…" : "Approve anyway (no AR)"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CC-068 (2026-09-25) — platform-admin approval-override modal.
           Distinct amber styling so nobody confuses this with a real
           approver click. Requires a written reason ≥ 20 chars; server
           audit-logs the action to admin_audit_log. */}
      {overrideOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
          data-testid="payroll-detail-override-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-[560px] w-[92vw] p-6 border-2 border-[#8a5a1a]">
            <div className="flex items-start gap-3 mb-3">
              <AlertCircle size={22} className="text-[#8a5a1a] shrink-0 mt-0.5" />
              <div>
                <div className="font-display font-bold text-[15px] text-[#8a5a1a]">
                  Platform-admin approval override
                </div>
                <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-1 leading-relaxed">
                  This closes the run out as approved <b>without a real approver click</b> and
                  bypasses the Send-for-Approval + SPLIT_JOB upload gates.
                  Every UI reading this run will render an amber "OVERRIDE" badge
                  instead of the usual "Approved by" pill. Use ONLY when the approver
                  attested out-of-band (e.g. PDF review email, Slack thread) and the
                  in-app click never happened.
                </div>
              </div>
            </div>
            <label className="block text-[12px] font-semibold text-[#0e1420] mb-1">
              Written justification (audit-logged, ≥ 20 characters)
            </label>
            <textarea
              data-testid="payroll-detail-override-reason-textarea"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              rows={3}
              placeholder="e.g. Nathanael approved via PDF review email 2026-09-22; Slack thread #payroll linked."
              className="w-full border border-[#cfd4dc] rounded px-3 py-2 text-[13px] font-mono-data resize-none focus:outline-none focus:border-[#8a5a1a]"
            />
            <div className="flex items-center justify-between mt-1 text-[11px]">
              <span className={overrideReason.trim().length < 20 ? "text-[#a01d1d]" : "text-[color:var(--cc-ink-muted)]"}>
                {overrideReason.trim().length}/20 min
              </span>
              <span className="text-[color:var(--cc-ink-muted)] italic">Recorded to admin_audit_log</span>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setOverrideOpen(false); setOverrideReason(""); }}
                disabled={overrideSubmitting}
                data-testid="payroll-detail-override-cancel-btn"
                className="text-[12.5px] px-3 py-1.5 rounded border border-[#cfd4dc] font-semibold text-[#0e1420] hover:bg-[#f4f6fa] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doApproveOverride}
                disabled={overrideReason.trim().length < 20 || overrideSubmitting}
                data-testid="payroll-detail-override-submit-btn"
                className="text-[12.5px] px-3 py-1.5 rounded font-semibold text-white bg-[#8a5a1a] hover:bg-[#6a441a] disabled:opacity-50"
              >
                {overrideSubmitting ? "Overriding…" : "Confirm override (CC-068)"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────── Paylines table
// CC-366: display-only layout. Labels/values still come from lib/payrollView (PDF parity); only headers are shortened.
const SHORT_HDR = { "Commissionable": "Comm. base", "Commission before CSR": "Pre-CSR", "Min wage supplement": "Min wage", "Attributed $": "Attributed" };
const PAYLINE_ADJ = ["TFS", "CC Fee", "Permits", "Surges", "Materials"];
const JOB_ADJ = ["Permits", "Surges", "TFS", "CC"];
const slugLabel = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-");
const STICKY_TH = "sticky top-0 z-20 bg-[#f5f6f8]";

// Splits [label, text] cells: adjustment cells collapse into one slot at the first adjustment's position.
export function splitAdjCells(cells, adjLabels, adjOpen) {
  const adj = cells.filter(([l]) => adjLabels.includes(l));
  if (adjOpen || !adj.length) return { cols: cells.map((c) => ({ cell: c })), adj };
  const out = [];
  cells.forEach((c) => {
    if (!adjLabels.includes(c[0])) out.push({ cell: c });
    else if (c === adj[0]) out.push({ adj: true });
  });
  return { cols: out, adj };
}

// Collapsed: one "Adj." header. Open: the first adjustment column's header carries the collapse control.
function AdjHeader({ open, label, onToggle, testId, className }) {
  return (
    <th className={`${className} text-right`}>
      <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={testId} aria-expanded={!!open}
              title={open ? "Collapse adjustments into one column" : "Show each adjustment as its own column"}
              className="inline-flex items-center gap-0.5 uppercase hover:text-[#0072bc]">
        {open && <ChevronRight size={11} className="rotate-180" />}{open ? label : "Adj."}{!open && <ChevronRight size={11} />}
      </button>
    </th>
  );
}

function AdjCell({ adj, onToggle, testId, className }) {
  return (
    <td className={className} data-testid={testId}>
      <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} title="Click to show each adjustment as its own column"
              className="w-full text-right leading-[1.25] hover:text-[#0072bc]">
        {adj.map(([l, t]) => (
          <div key={l} className="text-[10.5px] font-mono-data whitespace-nowrap">
            <span className="text-[color:var(--cc-ink-muted)]">{l === "CC Fee" ? "CC" : l}</span> {t}
          </div>
        ))}
      </button>
    </td>
  );
}

export function PaylinesTable({ paylines, expanded, setExpanded, runId, slug, mode, onJobToggled }) {
  const [adjOpen, setAdjOpen] = useState(false);
  const scrollRef = useRef(null);
  const [boxW, setBoxW] = useState(0);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => setBoxW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [paylines?.length]);
  if (!paylines?.length) return null;
  // CC-189/CC-178: TFS column only where the run carries it; columns come from lib/payrollView (PDF mirrors it).
  const showTfs = hasTfs(paylines, "weekly_tfs");
  const withMinWage = showMinWage(paylines);  // CC-156
  const { cols: hdrCols } = splitAdjCells(paylineMoneyCells({}, showTfs, withMinWage), PAYLINE_ADJ, adjOpen);
  const toggleAdj = () => setAdjOpen(!adjOpen);
  const firstAdj = adjOpen ? hdrCols.find((c) => PAYLINE_ADJ.includes(c.cell[0]))?.cell[0] : null;
  const th = `${STICKY_TH} py-2 px-2 whitespace-nowrap`;
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="payroll-detail-paylines">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px]">
        Tech Paylines · {paylines.length}
      </div>
      <div ref={scrollRef} className="cc-scroll-visible overflow-auto max-h-[80vh]" data-testid="payroll-detail-paylines-scroll">
      <table className="w-full text-[12.5px]">
        <thead className="text-[10px] uppercase tracking-[.06em] text-[color:var(--cc-ink-muted)]">
          <tr>
            <th className={`${th} text-left left-0 z-30 pl-3`} data-testid="payroll-detail-paylines-th-tech">Tech</th>
            <th className={`${th} text-left`}>Tier</th>
            {hdrCols.map((c) => c.adj
              ? <AdjHeader key="adj" open={false} onToggle={toggleAdj} testId="payroll-detail-paylines-adj-toggle" className={th} />
              : c.cell[0] === firstAdj
                ? <AdjHeader key={c.cell[0]} open label={c.cell[0]} onToggle={toggleAdj} testId="payroll-detail-paylines-adj-toggle" className={th} />
                : <th key={c.cell[0]} className={`${th} text-right`} title={c.cell[0]}
                      data-testid={`payroll-detail-paylines-th-${slugLabel(c.cell[0])}`}>{SHORT_HDR[c.cell[0]] || c.cell[0]}</th>)}
            <th className={`${th} text-center`}>Jobs</th>
          </tr>
        </thead>
        <tbody>
          {paylines.map((p) => {
            const isOpen = !!expanded[p.person_id];
            const { cols, adj } = splitAdjCells(paylineMoneyCells(p, showTfs, withMinWage), PAYLINE_ADJ, adjOpen);
            return (
              <Fragment key={p.person_id}>
                <tr
                  className="group border-t border-[#eef1f6] hover:bg-[#f9fafb] cursor-pointer"
                  onClick={() => setExpanded({ ...expanded, [p.person_id]: !isOpen })}
                  data-testid={`payroll-detail-payline-${p.person_id}`}
                >
                  <td className="sticky left-0 z-10 bg-white group-hover:bg-[#f9fafb] py-2 pl-3 pr-2 border-r border-[#eef1f6]" data-testid={`payroll-detail-tech-cell-${p.person_id}`}>
                    <div className="flex items-start gap-1.5">
                      <ChevronRight size={13} className={`mt-0.5 shrink-0 text-[color:var(--cc-ink-muted)] transition-transform ${isOpen ? "rotate-90" : ""}`} />
                      <div className="min-w-0">
                        <div className="font-semibold whitespace-nowrap">{p.name}</div>
                        <div className="text-[10.5px] text-[color:var(--cc-ink-muted)] whitespace-nowrap">{techSubLabel(p)}</div>
                        <div className={`text-[10px] max-w-[190px] leading-tight ${Number(p.material_overage_deduction || 0) > 0 ? "text-[#9a5b00] font-semibold" : "text-[color:var(--cc-ink-muted)]"}`}
                             data-testid={`payroll-detail-materials-line-${p.person_id}`}>{materialsLine(p)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-2 px-2 text-[11px]" data-testid={`payroll-detail-tier-${p.person_id}`} title={p.tier_reason || undefined}>
                    <div className="font-mono-data font-semibold whitespace-nowrap">{tierLabel(p)}</div>
                    {p.rate_override_pct != null && (
                      <div className="mt-1 inline-flex px-1.5 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap bg-[#e8f3fb] text-[#005a94] border-[#b9dcf2]"
                           data-testid={`payroll-detail-rate-override-${p.person_id}`}>
                        Override {p.rate_override_pct}%
                      </div>
                    )}
                    {p.alt_key?.label && (
                      <div className={`mt-1 inline-flex px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${p.alt_key.rate_applies ? "bg-[#fff4e5] text-[#9a5b00] border-[#f5d49a]" : "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]"}`}
                           data-testid={`payroll-detail-altkey-${p.person_id}`}>
                        {p.alt_key.label}
                      </div>
                    )}
                  </td>
                  {cols.map((c) => c.adj
                    ? <AdjCell key="adj" adj={adj} onToggle={toggleAdj} testId={`payroll-detail-cell-${p.person_id}-adjustments`} className="py-2 px-2 text-right" />
                    : (
                    <td key={c.cell[0]} className={`py-2 px-2 text-right font-mono-data whitespace-nowrap ${c.cell[0] === "Commission" ? "font-bold text-[#0e1420]" : ""}`}
                        data-testid={`payroll-detail-cell-${p.person_id}-${slugLabel(c.cell[0])}`}>
                      {c.cell[1]}
                      {c.cell[0] === "Commission" && p.carried_total != null && (
                        <div className="text-[10px] font-semibold text-[#14532d] whitespace-normal" data-testid={`payroll-detail-carried-${p.person_id}`}>
                          + carried {money(p.carried_total)}
                          {(p.carried_lines || []).some((x) => x.flag === "paid_outside") && " · paid outside tool"}
                        </div>
                      )}
                    </td>
                  ))}
                  <td className="py-2 px-2 text-center text-[color:var(--cc-ink-muted)]">{p.job_lines?.length ?? 0}</td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={hdrCols.length + 3} className="bg-[#f9fafb] border-t border-[#eef1f6] p-3" data-testid={`payroll-detail-joblines-${p.person_id}`}>
                      {/* Pinned to the visible card width so the sub-table scrolls on its own instead of widening the main table */}
                      <div className={boxW ? "sticky left-3" : "w-0 min-w-full"} style={boxW ? { width: boxW - 24 } : undefined}>
                      {p.tier_reason && (
                        <div className="mb-1.5 text-[11.5px] text-[color:var(--cc-ink-muted)]" data-testid={`payroll-detail-tier-reason-${p.person_id}`}>
                          <span className="font-semibold">Tier:</span> {p.tier_reason}
                        </div>
                      )}
                      <div className="mb-1.5 text-[11.5px] text-[#0e1420]" data-testid={`payroll-detail-materials-detail-${p.person_id}`}>{materialsLine(p)}</div>
                      <div className="mb-3 text-[12px] font-mono-data text-[#0e1420]" data-testid={`payroll-detail-formula-${p.person_id}`}>{formulaLine(p)}</div>
                      <JobLinesTable rows={p.job_lines || []} payline={p} runId={runId} slug={slug} mode={mode} onToggled={onJobToggled} />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      </div>
    </div>
  );
}

function JobLinesTable({ rows: rawRows, payline, runId, slug, mode, onToggled }) {
  const { jobs: rows, line: overage } = overageView({ ...(payline || {}), job_lines: rawRows });  // CC-268
  const [busyToggle, setBusyToggle] = useState(null);
  const [adjOpen, setAdjOpen] = useState(false);  // CC-366
  // CC-094 (2026-09-29) — inline Materials $ override state. `editingJob`
  // tracks which row's Materials cell is in edit mode; `matDraft` holds
  // the pending value while the admin types. Persistence hits
  // PATCH /runs/{runId}/jobs/{job_number}/material-cost-override; on
  // success we call onToggled() so the parent re-fetches + re-computes.
  const [editingJob, setEditingJob] = useState(null);
  const [matDraft, setMatDraft] = useState("");
  const [savingMat, setSavingMat] = useState(null);
  const canToggleCsr = mode === "real";
  const canEditMaterials = mode === "real";
  const openMatEdit = (r) => {
    if (!canEditMaterials) return;
    setEditingJob(r.job_number);
    setMatDraft(String(r.materials ?? "0"));
  };
  const saveMatOverride = async (jobNumber) => {
    const parsed = parseFloat(matDraft);
    if (Number.isNaN(parsed) || parsed < 0) {
      window.alert("Materials $ must be a non-negative number.");
      return;
    }
    setSavingMat(jobNumber);
    try {
      await api.patch(
        `/payroll/runs/${runId}/jobs/${jobNumber}/material-cost-override?tenant_slug=${slug}`,
        { materials_cost: parsed },
      );
      setEditingJob(null);
      setMatDraft("");
      if (onToggled) await onToggled();
    } catch (e) {
      window.alert(e?.response?.data?.detail || e.message);
    } finally {
      setSavingMat(null);
    }
  };
  const toggleCsr = async (jobNumber, currentlyMarked) => {
    if (!canToggleCsr) return;
    setBusyToggle(jobNumber);
    try {
      await api.post(
        `/payroll/runs/${runId}/csr-converted-toggle?tenant_slug=${slug}`,
        { job_number: jobNumber, action: currentlyMarked ? "unmark" : "mark" },
      );
      if (onToggled) await onToggled();
    } catch (e) {
      window.alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusyToggle(null);
    }
  };
  if (!rows.length) return <div className="text-[12px] text-[color:var(--cc-ink-muted)]">No job lines.</div>;
  const showTfs = hasTfs(rows, "job_tfs");
  const { cols: restHdr } = splitAdjCells(jobLineMoneyCells({}, showTfs).slice(2), JOB_ADJ, adjOpen);
  const firstAdj = adjOpen ? restHdr.find((c) => JOB_ADJ.includes(c.cell[0]))?.cell[0] : null;
  const toggleAdj = () => setAdjOpen(!adjOpen);
  const th = "sticky top-0 z-20 bg-[#f9fafb] py-1.5 px-2 whitespace-nowrap";
  return (
    <div className="cc-scroll-visible overflow-auto max-h-[60vh] border border-[#eef1f6] rounded bg-white" data-testid={`payroll-detail-joblines-scroll-${payline?.person_id}`}>
    <table className="w-full text-[11.5px]">
      <thead className="text-[10px] uppercase tracking-[.06em] text-[color:var(--cc-ink-muted)]">
        <tr>
          <th className={`${th} text-left left-0 z-30`} data-testid="payroll-detail-joblines-th-job">Job #</th>
          <th className={`${th} text-left`}>BU</th>
          <th className={`${th} text-right`}>Invoice</th>
          <th className={`${th} text-right`}>Materials</th>
          {restHdr.map((c) => c.adj
            ? <AdjHeader key="adj" open={false} onToggle={toggleAdj} testId={`payroll-detail-joblines-adj-toggle-${payline?.person_id}`} className={th} />
            : c.cell[0] === firstAdj
              ? <AdjHeader key={c.cell[0]} open label={c.cell[0]} onToggle={toggleAdj} testId={`payroll-detail-joblines-adj-toggle-${payline?.person_id}`} className={th} />
              : <th key={c.cell[0]} className={`${th} text-right`} title={c.cell[0]}>{SHORT_HDR[c.cell[0]] || c.cell[0]}</th>)}
          <th className={`${th} text-center`}>CSR?</th>
          <th className={`${th} text-center`}>NTF?</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Fragment key={r.job_number}>
            <tr className="border-t border-[#eef1f6]" data-testid={`payroll-detail-jobline-${r.job_number}`}>
              <td className="sticky left-0 z-10 bg-white py-1.5 px-2 font-mono-data font-semibold whitespace-nowrap border-r border-[#eef1f6]">
                {r.job_number}
                {r.split_meta && (
                  <span
                    data-testid={`payroll-detail-split-badge-${r.job_number}`}
                    title={`Split ${r.split_meta.working_hours_this_tech}h of ${r.split_meta.working_hours_total}h across ${r.split_meta.all_techs.length} techs`}
                    className="ml-1.5 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-[#fef3d9] text-[#8a5a1a] border border-[#e6c99a]"
                  >
                    Split {(r.split_meta.share_pct * 100).toFixed(0)}%
                  </span>
                )}
                {r.csr_converted && (
                  <span
                    data-testid={`payroll-detail-csr-badge-${r.job_number}`}
                    title={`CSR conversion — $${r.csr_slice_deducted} deducted from SP pool, distributed to active CSR staff`}
                    className="ml-1 inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-[#e7f4fb] text-[#1c6a99] border border-[#c2dff0]"
                  >
                    CSR
                  </span>
                )}
              </td>
              <td className="py-1.5 px-2 text-[11px]" data-testid={`payroll-detail-jobline-bu-${r.job_number}`}>{r.business_unit || "—"}</td>
              <td className="py-1.5 px-2 text-right font-mono-data">{money(r.invoice_total)}</td>
              <td className="py-1.5 px-2 text-right font-mono-data" data-testid={`payroll-detail-jobline-materials-cell-${r.job_number}`}>
                {canEditMaterials && editingJob === r.job_number ? (
                  <input
                    type="number" step="0.01" min="0" autoFocus
                    value={matDraft}
                    onChange={(e) => setMatDraft(e.target.value)}
                    onBlur={() => saveMatOverride(r.job_number)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveMatOverride(r.job_number);
                      if (e.key === "Escape") { setEditingJob(null); setMatDraft(""); }
                    }}
                    disabled={savingMat === r.job_number}
                    data-testid={`payroll-detail-jobline-materials-input-${r.job_number}`}
                    className="w-24 text-right px-1.5 py-0.5 border border-[#0072bc] rounded font-mono-data text-[12px] outline-none"
                  />
                ) : canEditMaterials ? (
                  <button
                    type="button"
                    onClick={() => openMatEdit(r)}
                    data-testid={`payroll-detail-jobline-materials-btn-${r.job_number}`}
                    title={r.material_override ? `Manual override — set by ${r.material_override.set_by_email || 'admin'}. Click to edit.` : "Click to override Materials $"}
                    className={`inline-flex items-center gap-1 px-1 -mx-1 rounded hover:bg-[#e7f4fb] cursor-pointer ${
                      r.material_override?.has_upload_conflict
                        ? "text-[#8a5a1a]"
                        : r.material_override
                          ? "text-[#1c6a99] font-semibold"
                          : ""
                    }`}
                  >
                    {money(r.materials)}
                    {r.material_override?.has_upload_conflict && (
                      <span title={`Latest st_job_costing upload had $${r.material_override.upload_value_at_conflict?.toFixed(2)} — kept the manual override; click to review`} className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-[#fef3d9] text-[#8a5a1a] border border-[#e6c99a]">!</span>
                    )}
                    {r.material_override && !r.material_override.has_upload_conflict && (
                      <span title="Manual override active" className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-[#e7f4fb] text-[#1c6a99] border border-[#c2dff0]">M</span>
                    )}
                  </button>
                ) : (
                  money(r.materials)
                )}
              </td>
              {(() => {
                const { cols, adj } = splitAdjCells(jobLineMoneyCells(r, showTfs).slice(2), JOB_ADJ, adjOpen);
                return cols.map((c) => c.adj
                  ? <AdjCell key="adj" adj={adj} onToggle={toggleAdj} testId={`payroll-detail-jobline-adjustments-${r.job_number}`} className="py-1.5 px-2 text-right" />
                  : <td key={c.cell[0]} className={`py-1.5 px-2 text-right font-mono-data whitespace-nowrap ${c.cell[0] === "Attributed $" ? "font-bold" : ""}`}
                        data-testid={`payroll-detail-jobline-cell-${r.job_number}-${slugLabel(c.cell[0])}`}>{c.cell[1]}</td>);
              })()}
              <td className="py-1.5 px-2 text-center">
                {canToggleCsr ? (
                  <button
                    type="button"
                    onClick={() => toggleCsr(r.job_number, !!r.csr_converted)}
                    disabled={busyToggle === r.job_number}
                    data-testid={`payroll-detail-csr-toggle-${r.job_number}`}
                    title={r.csr_converted
                      ? "Unmark as CSR-converted (removes this job from the CSR pool on the next compute)"
                      : "Mark as CSR-converted (adds this job to the CSR pool on the next compute)"}
                    className={`inline-flex items-center justify-center w-5 h-5 rounded border transition-colors ${
                      r.csr_converted
                        ? "bg-[#0072bc] border-[#0072bc] text-white hover:bg-[#005a94]"
                        : "bg-white border-[#cfd4dc] text-transparent hover:border-[#0072bc]"
                    } disabled:opacity-50 disabled:cursor-wait`}
                  >
                    {busyToggle === r.job_number ? "…" : (r.csr_converted ? "✓" : "○")}
                  </button>
                ) : (
                  <span>{r.csr_converted ? "✓" : ""}</span>
                )}
              </td>
              <td className="py-1.5 px-2 text-center">{r.job_is_ntf ? "✓" : ""}</td>
            </tr>
            {r.split_meta && (
              <tr data-testid={`payroll-detail-split-breakdown-${r.job_number}`}>
                <td colSpan={restHdr.length + 6} className="py-2 px-4 bg-[#fef8ea] border-t border-[#f0e0b9]">
                  <div className="text-[11px] text-[#8a5a1a] font-semibold mb-1.5">
                    Split job math — original invoice ${r.split_meta.original_invoice_total.toFixed(2)}, split by working hours:
                  </div>
                  <table className="w-full text-[11.5px]">
                    <thead className="text-[10px] uppercase tracking-wider text-[#8a5a1a]">
                      <tr>
                        <th className="text-left py-1 px-2 font-semibold">Tech</th>
                        <th className="text-right py-1 px-2 font-semibold">Working hours</th>
                        <th className="text-right py-1 px-2 font-semibold">Share %</th>
                        <th className="text-right py-1 px-2 font-semibold">Attributed invoice $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(r.split_meta.per_tech_breakdown || []).map((b) => {
                        const attrDollars = r.split_meta.original_invoice_total * b.share_pct;
                        return (
                          <tr key={b.tech} className="border-t border-[#f0e0b9]">
                            <td className="py-1 px-2 font-semibold">{b.tech}</td>
                            <td className="py-1 px-2 text-right font-mono-data">{b.working_hours.toFixed(2)}h</td>
                            <td className="py-1 px-2 text-right font-mono-data">{(b.share_pct * 100).toFixed(1)}%</td>
                            <td className="py-1 px-2 text-right font-mono-data">{money(attrDollars)}</td>
                          </tr>
                        );
                      })}
      </tbody>
                  </table>
                </td>
              </tr>
            )}
          </Fragment>
        ))}
        {overage && (
          <tr className="border-t-2 border-[#e6c99a] bg-[#fef8ea]" data-testid={`payroll-detail-overage-line-${payline?.person_id}`}>
            <td className="py-1.5 px-2 font-semibold text-[#8a5a1a]" colSpan={2}>{OVERAGE_LABEL}</td>
            <td className="py-1.5 px-2 text-[11px] text-[#8a5a1a] font-mono-data" colSpan={restHdr.length + 1} data-testid={`payroll-detail-overage-detail-${payline?.person_id}`}>{overageLineText(overage)}</td>
            <td className="py-1.5 px-2 text-right font-mono-data font-bold text-[#8a5a1a]" data-testid={`payroll-detail-overage-amount-${payline?.person_id}`}>{fmtMoney(overage.commission_removed)}</td>
            <td colSpan={2} />
          </tr>
        )}
        {overage?.mismatch != null && (
          <tr className="bg-[#fdecec]" data-testid={`payroll-detail-overage-mismatch-${payline?.person_id}`}>
            <td colSpan={restHdr.length + 6} className="py-1.5 px-2 text-[11.5px] font-semibold text-[#7a1c1c]">{overageMismatchText(overage)}</td>
          </tr>
        )}
      </tbody>
    </table>
    </div>
  );
}

// ──────────────────────────────────────────────────────── Exceptions
/* CC-195 — Form 21330 rows the VPS ingest sent with review=true. */
function FormReviewSection({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="payroll-detail-form-review">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px]">
        Form 21330 · flagged for review · {rows.length}
      </div>
      <ul className="divide-y divide-[#eef1f6] text-[12.5px]">
        {rows.map((r) => (
          <li key={r.job_number} className="px-5 py-2 flex flex-wrap gap-x-3" data-testid={`payroll-detail-form-review-${r.job_number}`}>
            <span>Job <code className="font-mono-data">{r.job_number}</code></span>
            <span className="font-semibold">${Number(r.material_total || 0).toFixed(2)}</span>
            <span className="text-[#5b6474]">{r.submitter_name || "unknown submitter"} · {r.submitted_at?.slice(0, 16).replace("T", " ")}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExceptionsPanel({ exceptions }) {
  const grouped = useMemo(() => {
    const g = {};
    for (const e of exceptions || []) (g[e.code] ||= []).push(e);
    return g;
  }, [exceptions]);
  if (!exceptions?.length) return null;
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="payroll-detail-exceptions">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px]">
        Exceptions · {exceptions.length}
      </div>
      <div className="divide-y divide-[#eef1f6]">
        {Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([code, items]) => {
          const isBlocking = BLOCKING_CODES.has(code);
          return (
            <div key={code} className="px-5 py-3">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${
                    isBlocking ? "bg-[#fdecec] text-[#7a1c1c]" : "bg-[#fef3d9] text-[#8a5a1a]"
                  }`}
                  data-testid={`payroll-detail-exc-code-${code}`}
                >
                  {isBlocking ? "Blocking" : "Advisory"}
                </span>
                <span className="font-mono-data text-[12.5px] font-semibold">{code}</span>
                <span className="text-[11.5px] text-[color:var(--cc-ink-muted)]">× {items.length}</span>
              </div>
              <ul className="ml-2 space-y-1 text-[12.5px]">
                {items.map((e, i) => (
                  <li key={i} className="text-[color:var(--cc-ink-muted)]">
                    <span className="font-semibold text-[#0e1420]">
                      {e.tech || e.job_number || "—"}
                    </span>
                    {e.job_numbers && <> · jobs {e.job_numbers.join(", ")}</>}
                    {e.weekly_revenue != null && <> · {money(e.weekly_revenue)}</>}
                    {e.detail && <div className="text-[11.5px] italic mt-0.5">{e.detail}</div>}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────── HOURS_SUGGEST_SPLIT decisions (pt.84.3)
function HoursSuggestSplitPanel({ runId, slug, mode, suggestions, onDecision }) {
  const [busyJob, setBusyJob] = useState(null);
  const [err, setErr] = useState(null);
  const base = mode === "real" ? "runs" : "test-runs";

  const decide = async (jobNumber, decision) => {
    setBusyJob(`${jobNumber}:${decision}`);
    setErr(null);
    try {
      // hours-split-decision endpoint is only registered on the real /runs router.
      // For test runs, the endpoint doesn't exist — surface a friendly error.
      if (mode !== "real") {
        throw new Error("Decisions persist only on real payroll runs, not sandbox test runs.");
      }
      await api.post(
        `/payroll/${base}/${runId}/hours-split-decision?tenant_slug=${slug}`,
        { job_number: jobNumber, decision },
      );
      await onDecision();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusyJob(null);
    }
  };

  return (
    <div className="bg-[#fef8ea] border border-[#f0e0b9] rounded-xl overflow-hidden" data-testid="payroll-detail-hours-suggest-splits">
      <div className="px-5 py-3 border-b border-[#f0e0b9] font-display font-semibold text-[14px] text-[#8a5a1a] flex items-center justify-between">
        <span>Hours file suggests possible splits · {suggestions.length}</span>
        <span className="text-[11px] font-normal">Decide per job — decisions persist across recomputes</span>
      </div>
      <div className="divide-y divide-[#f0e0b9]">
        {suggestions.map((s) => (
          <div key={s.job_number} className="px-5 py-3" data-testid={`payroll-detail-hours-suggest-${s.job_number}`}>
            <div className="text-[13px] mb-1.5">
              <span className="font-mono-data font-semibold">Job {s.job_number}</span>
              <span className="text-[color:var(--cc-ink-muted)] ml-2">
                · ST primary <b>{s.st_primary_tech}</b> ({s.st_primary_working_hours}h) · invoice ${s.invoice_total_st_authoritative.toFixed(2)}
              </span>
            </div>
            <div className="text-[12px] text-[#8a5a1a] mb-2">
              Co-worker(s) logged Working: {s.co_workers.map((c) => `${c.tech} (${c.working_hours}h)`).join(", ")}
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => decide(s.job_number, "accept_as_split")}
                disabled={!!busyJob}
                data-testid={`payroll-detail-decide-accept-${s.job_number}`}
                className="px-2.5 py-1.5 text-[11.5px] font-semibold rounded bg-[#0e8a68] text-white hover:bg-[#0a6f53] disabled:opacity-50"
              >
                {busyJob === `${s.job_number}:accept_as_split` ? "…" : "Accept as Split"}
              </button>
              <button
                onClick={() => decide(s.job_number, "keep_solo")}
                disabled={!!busyJob}
                data-testid={`payroll-detail-decide-solo-${s.job_number}`}
                className="px-2.5 py-1.5 text-[11.5px] font-semibold rounded bg-[#1c6a99] text-white hover:bg-[#155073] disabled:opacity-50"
              >
                {busyJob === `${s.job_number}:keep_solo` ? "…" : "Keep as Solo"}
              </button>
              <button
                onClick={() => decide(s.job_number, "snooze")}
                disabled={!!busyJob}
                data-testid={`payroll-detail-decide-snooze-${s.job_number}`}
                className="px-2.5 py-1.5 text-[11.5px] font-semibold rounded bg-[#6b7280] text-white hover:bg-[#4b5563] disabled:opacity-50"
              >
                {busyJob === `${s.job_number}:snooze` ? "…" : "Snooze (until recompute)"}
              </button>
            </div>
          </div>
        ))}
        {err && (
          <div className="px-5 py-2 text-[12px] text-[#7a1c1c] bg-[#fdecec]" data-testid="payroll-detail-hours-suggest-err">
            {typeof err === "string" ? err : JSON.stringify(err)}
          </div>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────── SPLIT_JOB Preflight (CC-022a/b)
function SplitPreflightSection({ attributed, unresolved, hasStSplits }) {
  const attRows = attributed || [];
  const unRows = unresolved || [];
  if (!attRows.length && !unRows.length) return null;
  const stCount = attRows.filter((r) => r.source === "st_report").length;
  const hoursCount = attRows.filter((r) => r.source !== "st_report").length;
  return (
    <div
      className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden"
      data-testid="payroll-detail-split-preflight"
    >
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex items-center justify-between">
        <span>SPLIT_JOB Preflight · verify against ServiceTitan</span>
        <div className="flex items-center gap-2">
          {stCount > 0 && (
            <span
              className="text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-[#e0f4ec] text-[#0e6b52]"
              data-testid="payroll-detail-split-preflight-st-count"
            >
              {stCount} ST-authoritative
            </span>
          )}
          {hoursCount > 0 && (
            <span
              className="text-[10.5px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-[#fef3d9] text-[#8a5a1a]"
              data-testid="payroll-detail-split-preflight-hours-count"
            >
              {hoursCount + unRows.length} needs verify
            </span>
          )}
        </div>
      </div>
      <div className="px-5 py-3 text-[11.5px] text-[color:var(--cc-ink-muted)] border-b border-[#eef1f6] leading-relaxed">
        {hasStSplits ? (
          <>ServiceTitan <b>Payroll (Detail) Report</b> is on file. ST rows below are authoritative — the delta column shows how they diverge from hours-inference. Hours-inferred rows still need manual verify.</>
        ) : (
          <>Compute infers per-tech splits from working-hours ratio because ST's Payroll (Detail) Report hasn't been uploaded. Upload it via the CC-022b dropzone above to switch to ST-authoritative percentages. Approval will be blocked until any SPLIT_JOB has an ST source.</>
        )}
      </div>
      {attRows.length > 0 && (
        <table className="w-full text-[13px]" data-testid="payroll-detail-split-preflight-attributed-table">
          <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
            <tr>
              <th className="text-left py-2 px-5">Job #</th>
              <th className="text-left py-2 px-5">Source</th>
              <th className="text-left py-2 px-5">Split</th>
              <th className="text-right py-2 px-5">Invoice total</th>
              <th className="text-left py-2 px-5">Δ vs hours</th>
            </tr>
          </thead>
          <tbody>
            {attRows.map((r) => {
              const src = r.source || "hours_ratio";
              const isSt = src === "st_report";
              return (
                <tr
                  key={r.job_number}
                  className="border-t border-[#eef1f6]"
                  data-testid={`payroll-detail-split-preflight-row-${r.job_number}`}
                >
                  <td className="py-2 px-5 font-mono-data font-bold">{r.job_number}</td>
                  <td className="py-2 px-5">
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded ${
                        isSt ? "bg-[#e0f4ec] text-[#0e6b52]" : "bg-[#fef3d9] text-[#8a5a1a]"
                      }`}
                      data-testid={`payroll-detail-split-source-${r.job_number}`}
                    >
                      {isSt ? "ST · authoritative" : "Hours · needs verify"}
                    </span>
                  </td>
                  <td className="py-2 px-5">
                    <div className="space-y-0.5">
                      {(r.breakdown || []).map((b) => (
                        <div key={b.tech} className="flex items-center gap-2">
                          <span className="font-semibold">{b.tech}</span>
                          {!isSt && (
                            <span className="font-mono-data text-[color:var(--cc-ink-muted)]">
                              {b.working_hours}h
                            </span>
                          )}
                          <span className="font-mono-data font-bold text-[#0e1420]">
                            {(Number(b.share_pct || 0) * 100).toFixed(1)}%
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-5 text-right font-mono-data font-bold">
                    {money(r.original_invoice_total)}
                  </td>
                  <td className="py-2 px-5">
                    {isSt && (r.breakdown || []).some((b) => b.hours_inferred_share_pct != null) ? (
                      <div className="space-y-0.5">
                        {(r.breakdown || []).map((b) => {
                          if (b.delta_pct_vs_hours == null) return null;
                          const abs = Math.abs(b.delta_pct_vs_hours * 100);
                          const sign = b.delta_pct_vs_hours >= 0 ? "+" : "−";
                          const flag = abs >= 5;
                          return (
                            <div key={b.tech}
                              className={`font-mono-data text-[11.5px] ${flag ? "text-[#7a1c1c] font-bold" : "text-[color:var(--cc-ink-muted)]"}`}
                              data-testid={`payroll-detail-split-delta-${r.job_number}-${b.tech}`}
                            >
                              {sign}{abs.toFixed(1)}pp
                            </div>
                          );
                        })}
                      </div>
                    ) : !isSt ? (
                      <span
                        className="inline-block px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide rounded bg-[#fef3d9] text-[#8a5a1a] border border-[#f0d999] cursor-help"
                        title="Open ServiceTitan → this invoice → side menu → Adjust Splits. Or upload the Technician Job Splits report to switch to ST-authoritative."
                      >
                        Verify in ST
                      </span>
                    ) : (
                      <span className="text-[11px] text-[color:var(--cc-ink-muted)] italic">no delta</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {unRows.length > 0 && (
        <div className="border-t border-[#eef1f6]">
          <div className="px-5 py-2 text-[11.5px] font-semibold text-[#7a1c1c] bg-[#fdecec]">
            Unresolved — cannot attribute (needs ST splits upload OR hours coverage)
          </div>
          <table className="w-full text-[13px]" data-testid="payroll-detail-split-preflight-unresolved-table">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-2 px-5">Job #</th>
                <th className="text-left py-2 px-5">Primary techs</th>
                <th className="text-left py-2 px-5">Fix</th>
              </tr>
            </thead>
            <tbody>
              {unRows.map((r) => (
                <tr
                  key={r.job_number}
                  className="border-t border-[#eef1f6]"
                  data-testid={`payroll-detail-split-preflight-unresolved-row-${r.job_number}`}
                >
                  <td className="py-2 px-5 font-mono-data font-bold">{r.job_number}</td>
                  <td className="py-2 px-5">{(r.techs || []).join(", ")}</td>
                  <td className="py-2 px-5">
                    <span
                      className="inline-block px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide rounded bg-[#fdecec] text-[#7a1c1c] border border-[#f7c1c1]"
                      title="Upload st_splits OR st_hours to resolve"
                    >
                      Upload ST splits or hours
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────── House Account bucket (CC-065)
function HouseAccountSection({ bucket }) {
  if (!bucket) return null;
  const techs = bucket.techs || [];
  if (!techs.length && !bucket.total_dollars) return null;
  return (
    <div
      className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden"
      data-testid="payroll-detail-house-account"
    >
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex items-center justify-between">
        <span>House Account · departed-tech revenue</span>
        <span className="text-[11px] font-normal text-[color:var(--cc-ink-muted)] italic">
          Auto-routed per policy — not paid out
        </span>
      </div>
      <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-[12.5px]">
        <div>
          <div className="uppercase tracking-[.09em] text-[10.5px] text-[color:var(--cc-ink-muted)]">
            Total revenue absorbed
          </div>
          <div className="font-mono-data font-bold text-[18px] mt-0.5" data-testid="payroll-detail-house-total">
            {money(bucket.total_dollars)}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[.09em] text-[10.5px] text-[color:var(--cc-ink-muted)]">
            Jobs
          </div>
          <div className="font-mono-data font-bold text-[18px] mt-0.5" data-testid="payroll-detail-house-jobs">
            {bucket.total_jobs || 0}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[.09em] text-[10.5px] text-[color:var(--cc-ink-muted)]">
            Departed techs
          </div>
          <div className="font-mono-data font-bold text-[18px] mt-0.5" data-testid="payroll-detail-house-techs-count">
            {techs.length}
          </div>
        </div>
      </div>
      {techs.length > 0 && (
        <table className="w-full text-[13px] border-t border-[#eef1f6]">
          <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
            <tr>
              <th className="text-left py-2 px-5">Departed tech</th>
              <th className="text-right py-2 px-5">Weekly revenue</th>
              <th className="text-left py-2 px-5">Jobs</th>
            </tr>
          </thead>
          <tbody>
            {techs.map((t) => (
              <tr
                key={t.person_id || t.name}
                className="border-t border-[#eef1f6]"
                data-testid={`payroll-detail-house-row-${t.person_id || t.name}`}
              >
                <td className="py-2 px-5 font-semibold">{t.name}</td>
                <td className="py-2 px-5 text-right font-mono-data font-bold">
                  {money(t.weekly_revenue)}
                </td>
                <td className="py-2 px-5 font-mono-data text-[11.5px] text-[color:var(--cc-ink-muted)]">
                  {(t.job_numbers || []).join(", ")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {bucket.policy_note && (
        <div className="px-5 py-2 text-[11px] text-[color:var(--cc-ink-muted)] italic border-t border-[#eef1f6]">
          {bucket.policy_note}
        </div>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────── CSR Commission (pt.82)
function CsrJobTable({ card, csr }) {
  const jobs = csr.per_job_breakdown || [];
  const notCounted = card?.not_counted || [];
  return (
    <div className="border-t border-[#eef1f6]" data-testid="payroll-detail-csr-jobs">
      {jobs.length > 0 && (
        <table className="w-full text-[12.5px]">
          <thead className="bg-[#f5f6f8] text-[10.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
            <tr>{CSR_JOB_HDR.map((h, i) => <th key={h} className={`py-2 px-3 ${i > 2 ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.job_number} className="border-t border-[#eef1f6]" data-testid={`payroll-detail-csr-job-${j.job_number}`}>
                {csrJobCells(j).map((c, i) => <td key={CSR_JOB_HDR[i]} className={`py-1.5 px-3 ${i > 2 ? "text-right font-mono-data" : ""}`}>{c}</td>)}
              </tr>
            ))}
            <tr className="border-t border-[#eef1f6] font-semibold">
              <td className="py-1.5 px-3" colSpan={7}>Total {card && card.ties_to_pool === false ? "(does not tie to pool)" : ""}</td>
              <td className="py-1.5 px-3 text-right font-mono-data" data-testid="payroll-detail-csr-jobs-total">{fmtMoney(card?.jobs_total ?? csr.total_dollars)}</td>
            </tr>
          </tbody>
        </table>
      )}
      {card?.base_note && <div className="px-5 py-2 text-[11px] text-[color:var(--cc-ink-muted)]">{card.base_note}</div>}
      {notCounted.length > 0 && (
        <div className="px-5 py-3 border-t border-[#eef1f6]" data-testid="payroll-detail-csr-not-counted">
          <div className="text-[12px] font-semibold mb-1">Logged CSR sales that did not count this week · {notCounted.length}</div>
          <table className="w-full text-[12px]">
            <thead className="text-[10.5px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)]">
              <tr>{CSR_NOT_COUNTED_HDR.map((h) => <th key={h} className="text-left py-1 px-2">{h}</th>)}</tr>
            </thead>
            <tbody>
              {notCounted.map((s) => (
                <tr key={`${s.job_number}-${s.csr_name}`} className="border-t border-[#eef1f6]">
                  {csrNotCountedCells(s).map((c, i) => <td key={CSR_NOT_COUNTED_HDR[i]} className="py-1 px-2">{c}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CsrCommissionSection({ csr, card }) {
  if (!csr) return null;
  const breakdown = csr.per_csr_breakdown || [];
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="payroll-detail-csr">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex items-center justify-between">
        <span>CSR Commission</span>
        <span className="text-[12px] text-[color:var(--cc-ink-muted)] font-normal">
          {csr.split_method}
        </span>
      </div>
      <div className="px-5 py-3 grid grid-cols-1 md:grid-cols-3 gap-4 text-[12.5px]">
        <div>
          <div className="uppercase tracking-[.09em] text-[10.5px] text-[color:var(--cc-ink-muted)]">Total pool</div>
          <div className="font-mono-data font-bold text-[18px] mt-0.5" data-testid="payroll-detail-csr-total">
            ${Number(csr.total_dollars || 0).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[.09em] text-[10.5px] text-[color:var(--cc-ink-muted)]">Rate applied</div>
          <div className="font-mono-data font-bold text-[18px] mt-0.5">
            {csr.csr_pct_used != null ? `${(csr.csr_pct_used * 100).toFixed(1)}%` : "—"}
          </div>
          <div className="text-[10.5px] text-[color:var(--cc-ink-muted)]">
            threshold ${csr.csr_threshold} · WTD ${csr.csr_converted_revenue_wtd || 0}
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[.09em] text-[10.5px] text-[color:var(--cc-ink-muted)]">Active CSR Staff</div>
          <div className="font-mono-data font-bold text-[18px] mt-0.5">
            {breakdown.length || 0}
          </div>
        </div>
      </div>
      {breakdown.length > 0 && (
        <table className="w-full text-[13px] border-t border-[#eef1f6]">
          <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
            <tr>
              <th className="text-left py-2 px-5">CSR</th>
              <th className="text-right py-2 px-5">Share %</th>
              <th className="text-right py-2 px-5">Share $</th>
            </tr>
          </thead>
          <tbody>
            {breakdown.map((b) => (
              <tr key={b.csr_profile_id} className="border-t border-[#eef1f6]" data-testid={`payroll-detail-csr-row-${b.csr_profile_id}`}>
                <td className="py-2 px-5 font-semibold">{b.name}</td>
                <td className="py-2 px-5 text-right font-mono-data">{(b.share_pct * 100).toFixed(1)}%</td>
                <td className="py-2 px-5 text-right font-mono-data font-bold">
                  ${Number(b.share_dollars).toLocaleString(undefined, {minimumFractionDigits:2, maximumFractionDigits:2})}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {breakdown.length === 0 && (
        <div className="px-5 py-3 text-[12px] text-[#8a5a1a] bg-[#fef3d9] border-t border-[#eef1f6]">
          No active CSR Staff on file. Add profiles at <a href="/admin/csr-staff" className="text-[#0072bc] font-semibold hover:underline" data-testid="payroll-detail-csr-link">/admin/csr-staff</a>.
        </div>
      )}
      <CsrJobTable card={card} csr={csr} />
    </div>
  );
}

// ──────────────────────────────────────────────────────── Pending Collection editor
const balancesToInputs = (saved) => Object.fromEntries(Object.entries(saved || {}).map(([k, v]) => [k, Number(v).toFixed(2)]));

function PendingCollectionEditor({ pending, overlayBalances, setOverlayBalances, onRecompute, busy, persisted, saved, dirty, setDirty, savedNote, frozen }) {
  if (!pending?.length) return null;
  const locked = persisted && frozen;  // CC-368: frozen/approved runs are read-only
  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-x-auto" data-testid="payroll-detail-pending">
      <div className="px-5 py-3 border-b border-[#eef1f6] font-display font-semibold text-[15px] flex items-center justify-between">
        <span>Pending Collection · {pending.length}</span>
        {locked ? (
          <span className="text-[11.5px] text-[color:var(--cc-ink-muted)] flex items-center gap-1" data-testid="payroll-detail-pending-locked">
            <Lock size={12} /> Run is frozen — balances are read-only
          </span>
        ) : (
        <button
          onClick={onRecompute}
          disabled={busy}
          data-testid="payroll-detail-recompute-btn"
          className="bg-[#0072bc] text-white text-[12px] font-semibold px-2.5 py-1 rounded hover:bg-[#005f9c] disabled:opacity-50 flex items-center gap-1.5"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Re-compute with these balances
        </button>
        )}
      </div>
      {persisted && savedNote && !Object.keys(dirty || {}).length && (
        <div className="px-5 py-1.5 text-[11.5px] font-semibold text-[#0e6b52] bg-[#e6f9f2] border-b border-[#eef1f6] flex items-center gap-1.5" data-testid="payroll-detail-pending-saved-note">
          <CheckCircle2 size={12} /> Saved · {savedNote.count} balance{savedNote.count === 1 ? "" : "s"}
        </div>
      )}
      <table className="w-full text-[13px]">
        <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
          <tr>
            <th className="text-left py-2 px-4">Job #</th>
            <th className="text-left py-2 px-4">Tech</th>
            <th className="text-right py-2 px-4">Invoice Total</th>
            <th className="text-left py-2 px-4">Reason</th>
            <th className="text-left py-2 px-4 w-[170px]">Auto-fill source</th>
            <th className="text-right py-2 px-4 w-[180px]">Set invoice_balance</th>
          </tr>
        </thead>
        <tbody>
          {pending.map((pc) => (
            <tr key={pc.job_number} className="border-t border-[#eef1f6]" data-testid={`payroll-detail-pending-row-${pc.job_number}`}>
              <td className="py-2 px-4 font-mono-data font-semibold">{pc.job_number}</td>
              <td className="py-2 px-4">{pc.primary_tech}</td>
              <td className="py-2 px-4 text-right font-mono-data">{money(pc.invoice_total)}</td>
              <td className="py-2 px-4 text-[11.5px] text-[color:var(--cc-ink-muted)]">{pc.reason}</td>
              <td className="py-2 px-4 text-[11.5px]" data-testid={`payroll-detail-pending-source-${pc.job_number}`}>
                <BalanceSourceBadge source={pc.invoice_balance_source} balance={pc.invoice_balance} />
              </td>
              <td className="py-2 px-4 text-right whitespace-nowrap">
                {persisted && saved?.[pc.job_number] != null && !dirty?.[pc.job_number] && (
                  <span className="mr-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#e6f9f2] text-[#0e6b52]"
                        data-testid={`payroll-detail-balance-saved-${pc.job_number}`}>Saved</span>
                )}
                <input
                  type="number" step="0.01" min="0"
                  placeholder="0.00 = collected"
                  value={overlayBalances[pc.job_number] ?? ""}
                  disabled={locked}
                  onChange={(e) => {
                    setOverlayBalances({ ...overlayBalances, [pc.job_number]: e.target.value });
                    if (persisted) setDirty({ ...dirty, [pc.job_number]: true });
                  }}
                  data-testid={`payroll-detail-balance-input-${pc.job_number}`}
                  className="border border-[#cfd4dc] rounded px-2 py-1 text-[12.5px] w-[140px] text-right font-mono-data disabled:bg-[#f5f6f8] disabled:text-[color:var(--cc-ink-muted)]"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="px-5 py-2 text-[11px] text-[color:var(--cc-ink-muted)] border-t border-[#eef1f6]">
        Blank = "unknown" (job stays deferred). Explicit 0.00 = "collected" (releases to payline). Non-zero = "still open" (stays deferred with that balance). Manual entry always overrides AR auto-fill.
      </div>
    </div>
  );
}

function BalanceSourceBadge({ source, balance }) {
  // pt.85 — explain WHERE each pending_collection balance came from.
  if (source === "manual")
    return <span className="inline-block bg-[#e7f4fb] text-[#1c6a99] px-1.5 py-0.5 rounded font-semibold" title="Manually set by editor overlay">Missie: {money(balance)}</span>;
  if (source === "ar_present")
    return <span className="inline-block bg-[#fef3d9] text-[#8a5a1a] px-1.5 py-0.5 rounded font-semibold" title="Job appeared in AR — Open Invoices report with this Balance">AR: {money(balance)}</span>;
  if (source === "ar_absent_in_window")
    return <span className="inline-block bg-[#e6f9f2] text-[#0e6b52] px-1.5 py-0.5 rounded font-semibold" title="Job invoiced inside AR window but absent from open-invoice list — inferred collected">AR: absent → $0</span>;
  if (source === "ar_coverage_gap")
    return <span className="inline-block bg-[#fdecec] text-[#7a1c1c] px-1.5 py-0.5 rounded font-semibold" title="Job invoice date falls outside the AR report window — cannot safely infer collected. Manually confirm or widen the AR pull.">AR gap — confirm</span>;
  return <span className="inline-block text-[color:var(--cc-ink-muted)]" title="No AR report uploaded and no manual balance set">unknown</span>;
}

// ──────────────────────────────────────────────────────── Team Review (pt.87)
function TeamReviewSection({ teamReview, loadErr, sendResult, onDismissSendResult, blockingCount, alreadySentForApproval, alreadyApproved, busy, onSend, onRefresh, runId, slug, canEdit }) {
  const reviews = teamReview?.reviews || [];
  const hasReviews = reviews.length > 0;
  // CC-070 fix (2026-11-24, post-mortem #2): `alreadySentForApproval`
  // was in this expression previously — it caused the Send button to
  // grey-out permanently the moment `sent_for_approval_at` was stamped
  // on the run, EVEN WHEN zero team reviews had actually been created
  // (pt.91.1 saga: prior failed attempts left the approval-send flag
  // set but wrote no rows to `payroll_team_reviews`). Team Review must
  // remain sendable in that state — its whole point is re-opening the
  // loop with the workforce. `alreadyApproved` still gates (recompute
  // is the correct path once finalized); `blockingCount` still gates
  // (numbers not stable). Approval-send status is now a tooltip note,
  // not a disable condition.
  const disabled =
    busy === "send-for-team-review" ||
    blockingCount > 0 ||
    alreadyApproved;
  let sendTooltip;
  if (blockingCount > 0) sendTooltip = `Resolve ${blockingCount} blocking exception${blockingCount === 1 ? "" : "s"} first.`;
  else if (alreadyApproved) sendTooltip = "This run is already approved. Recompute if the numbers changed.";
  else if (alreadySentForApproval && !hasReviews) sendTooltip = "Run was sent for approval, but no team reviews landed. Sending will re-open the loop with each tech + CSR.";
  else if (alreadySentForApproval) sendTooltip = "Run was sent for approval — re-trigger only refreshes links for people whose numbers changed. Untouched, still-pending links stay valid.";
  else sendTooltip = hasReviews
    ? "Re-trigger only refreshes links for people whose numbers changed. Untouched, still-pending links stay valid."
    : "Emails each eligible tech + CSR a single-use magic link to review their week before approval.";

  return (
    <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden" data-testid="payroll-detail-team-review">
      <div className="px-5 py-3 border-b border-[#eef1f6] flex items-center justify-between flex-wrap gap-2">
        <div className="font-display font-semibold text-[15px] flex items-center gap-2">
          <Users size={15} strokeWidth={1.9} className="text-[#0072bc]" /> Team Review
          {hasReviews && (
            <span data-testid="payroll-detail-team-review-count" className="text-[11px] font-normal text-[color:var(--cc-ink-muted)]">
              · {reviews.length} recipient{reviews.length === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasReviews && (
            <button
              onClick={onRefresh}
              data-testid="payroll-detail-team-review-refresh"
              className="text-[11.5px] font-semibold text-[color:var(--cc-ink-muted)] hover:text-[#0072bc] flex items-center gap-1"
            >
              <RefreshCw size={11} /> Refresh
            </button>
          )}
          <button
            onClick={onSend}
            disabled={disabled}
            title={sendTooltip}
            data-testid="payroll-detail-team-review-send-btn"
            className="bg-[#0e8a68] text-white text-[12.5px] font-semibold px-3 py-1.5 rounded-lg hover:bg-[#0a6f53] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Send size={12} /> {busy === "send-for-team-review"
              ? "Sending…"
              : hasReviews ? "Re-trigger team review" : "Send for team review"}
          </button>
        </div>
      </div>
      {loadErr && (
        <div
          className="px-5 py-2.5 bg-[#fdecec] border-b border-[#f7c1c1] text-[12px] text-[#7a1c1c] flex items-start gap-2"
          data-testid="payroll-detail-team-review-load-err"
        >
          <XCircle size={13} strokeWidth={1.9} className="mt-0.5" />
          <div>
            <div className="font-semibold">Panel data unavailable</div>
            <div className="text-[11.5px] font-mono-data mt-0.5 break-all">{loadErr}</div>
          </div>
        </div>
      )}
      {!hasReviews && (
        <div className="px-5 py-4 text-[13px] text-[color:var(--cc-ink-muted)]" data-testid="payroll-detail-team-review-empty">
          No team review sent yet for this run. Each eligible tech and active CSR will get a single-use link
          with a 4-hour response window. No response = auto-released to approver.
        </div>
      )}
      {sendResult && (
        <TeamReviewSendResultPanel result={sendResult} onDismiss={onDismissSendResult} />
      )}
      {hasReviews && (
        <>
          <div className={`px-5 py-2 text-[12px] flex items-center gap-2 border-b border-[#eef1f6] ${teamReview.can_send_for_approval ? "bg-[#e6f9f2] text-[#0e6b52]" : "bg-[#fef3d9] text-[#8a5a1a]"}`} data-testid="payroll-detail-team-review-gate">
            {teamReview.can_send_for_approval
              ? <><CheckCircle2 size={13} /> <span className="font-semibold">Team review complete — ok to send for approval.</span></>
              : <><Clock size={13} /> <span className="font-semibold">{teamReview.gate_reason || "Waiting on team responses…"}</span></>
            }
            <span className="ml-auto inline-flex items-center gap-3">
              <DeliverySummary summary={teamReview.delivery_summary} />
              {canEdit && <SendTestEmailButton runId={runId} slug={slug} />}
            </span>
          </div>
          <table className="w-full text-[13px]">
            <thead className="bg-[#f5f6f8] text-[10.5px] uppercase tracking-[.09em] text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-2 px-5">Name</th>
                <th className="text-left py-2 px-5">Type</th>
                <th className="text-left py-2 px-5">Status</th>
                <th className="text-left py-2 px-5">Email delivery</th>
                <th className="text-left py-2 px-5">Responded / expires</th>
                <th className="text-left py-2 px-5">Note</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((r) => (
                <tr key={r.id} className="border-t border-[#eef1f6]" data-testid={`payroll-detail-team-review-row-${r.subject_key}`}>
                  <td className="py-2 px-5">
                    <div className="font-semibold text-[#0e1420]">{r.subject_name}</div>
                    <div className="text-[10.5px] font-mono-data text-[color:var(--cc-ink-muted)]">{r.subject_email}</div>
                  </td>
                  <td className="py-2 px-5 text-[11.5px] text-[color:var(--cc-ink-muted)] uppercase tracking-wide">{r.subject_type}</td>
                  <td className="py-2 px-5"><StatusBadge status={r.effective_status || r.status} /></td>
                  <td className="py-2 px-5"><DeliveryChip delivery={r.delivery} testId={`payroll-detail-team-review-delivery-${r.subject_key}`} /></td>
                  <td className="py-2 px-5 text-[11.5px] font-mono-data text-[color:var(--cc-ink-muted)]">
                    {r.responded_at
                      ? <>responded {r.responded_at.slice(0,16).replace("T"," ")}</>
                      : <>expires {r.expires_at?.slice(0,16).replace("T"," ")}</>}
                  </td>
                  <td className="py-2 px-5 text-[12px] text-[#8a5a1a] italic max-w-[280px]" data-testid={`payroll-detail-team-review-note-${r.subject_key}`}>
                    {r.notes || ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-5 py-2 text-[11px] text-[color:var(--cc-ink-muted)] border-t border-[#eef1f6]">
            Hourly techs, no-rows techs, departed techs, and unmatched names are auto-excluded from links. Zero-CSR weeks skip CSR links.
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending:              { icon: Clock,         cls: "bg-[#fef3d9] text-[#8a5a1a]", label: "pending" },
    approved:             { icon: CheckCircle2,  cls: "bg-[#e6f9f2] text-[#0e6b52]", label: "approved" },
    approved_with_notes:  { icon: MessageSquare, cls: "bg-[#e7f4fb] text-[#1c6a99]", label: "approved w/ note" },
    auto_released:        { icon: Clock,         cls: "bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]", label: "auto-released (4h)" },
    invalidated:          { icon: XCircle,       cls: "bg-[#fdecec] text-[#7a1c1c]", label: "invalidated" },
    failed:               { icon: XCircle,       cls: "bg-[#fdecec] text-[#7a1c1c]", label: "failed" },
  };
  const spec = map[status] || { icon: Clock, cls: "bg-[#f5f6f8] text-[color:var(--cc-ink-muted)]", label: status };
  const Icon = spec.icon;
  return (
    <span data-testid={`team-review-status-${status}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded font-semibold text-[11px] ${spec.cls}`}>
      <Icon size={11} /> {spec.label}
    </span>
  );
}

// CC-070 (2026-09-25) — inline per-recipient send-result panel. Prior UI
// buried this in a window.alert with count-only summary, so prod incidents
// where 2 of 9 techs sent and 7 silently failed (or aborted mid-loop) had
// no in-app signal about who or why. Each recipient's dispatch status +
// error is now rendered as its own row, color-coded by outcome.
function TeamReviewSendResultPanel({ result, onDismiss }) {
  const summary = result?.dispatch_summary || [];
  const excluded = result?.excluded || [];
  const sent = result?.sent || 0;
  const alreadySent = result?.already_sent || 0;
  const failed = result?.failed || 0;
  const warning = result?.warning;
  const anyFailed = !!result?.any_failed;
  const allMocked = !!result?.all_mocked;

  const rowCls = (r) => {
    if (r.delivery_status === "failed") return "bg-[#fdecec]";
    if (r.mocked) return "bg-[#fef3d9]";
    return "bg-[#e6f9f2]";
  };
  const statusLabel = (r) => {
    if (r.delivery_status === "failed") return "FAILED";
    if (r.mocked) return "MOCKED";
    return (r.delivery_status || "unknown").toUpperCase();
  };
  const statusTextCls = (r) => {
    if (r.delivery_status === "failed") return "text-[#7a1c1c]";
    if (r.mocked) return "text-[#8a5a1a]";
    return "text-[#0e6b52]";
  };

  return (
    <div
      className="border-b border-[#eef1f6] bg-[#fafbfd] px-5 py-3"
      data-testid="payroll-detail-team-review-send-result"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12.5px] font-semibold text-[#0e1420]">
          Last send:{" "}
          <span className="text-[#0e6b52]" data-testid="team-review-send-result-sent">
            {sent} new
          </span>
          {" · "}
          <span className="text-[color:var(--cc-ink-muted)]" data-testid="team-review-send-result-already-sent">
            {alreadySent} unchanged
          </span>
          {" · "}
          <span
            className={failed > 0 ? "text-[#7a1c1c]" : "text-[color:var(--cc-ink-muted)]"}
            data-testid="team-review-send-result-failed"
          >
            {failed} failed
          </span>
          {" · "}
          <span
            className={excluded.length > 0 ? "text-[#8a5a1a]" : "text-[color:var(--cc-ink-muted)]"}
            data-testid="team-review-send-result-excluded"
          >
            {excluded.length} excluded
          </span>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          data-testid="team-review-send-result-dismiss"
          className="text-[11px] text-[color:var(--cc-ink-muted)] hover:text-[#0e1420]"
        >
          Dismiss
        </button>
      </div>
      {warning && (
        <div
          className={`rounded border px-2.5 py-1.5 text-[11.5px] mb-2 ${
            anyFailed || allMocked
              ? "bg-[#fdecec] border-[#f7c1c1] text-[#7a1c1c]"
              : "bg-[#fef3d9] border-[#f0d999] text-[#8a5a1a]"
          }`}
          data-testid="team-review-send-result-warning"
        >
          {warning}
        </div>
      )}
      {summary.length > 0 && (
        <div className="rounded border border-[#eef1f6] overflow-hidden mb-2">
          <table className="w-full text-[12px]" data-testid="team-review-send-result-summary-table">
            <thead className="bg-[#f5f6f8] text-[10.5px] uppercase tracking-[.08em] text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left py-1.5 px-2.5">Recipient</th>
                <th className="text-left py-1.5 px-2.5">Type</th>
                <th className="text-left py-1.5 px-2.5">Result</th>
                <th className="text-left py-1.5 px-2.5">Reason / message id</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((r) => (
                <tr
                  key={`${r.subject_type}:${r.subject_key}`}
                  className={`border-t border-[#eef1f6] ${rowCls(r)}`}
                  data-testid={`team-review-send-result-row-${r.subject_key}`}
                >
                  <td className="py-1.5 px-2.5 font-semibold text-[#0e1420]">{r.subject_name}</td>
                  <td className="py-1.5 px-2.5 text-[color:var(--cc-ink-muted)] uppercase tracking-wide text-[10.5px]">
                    {r.subject_type}
                  </td>
                  <td className={`py-1.5 px-2.5 font-bold text-[11px] ${statusTextCls(r)}`}>
                    {statusLabel(r)}
                  </td>
                  <td className="py-1.5 px-2.5 text-[11px] font-mono-data text-[color:var(--cc-ink-muted)] break-all">
                    {r.error || r.mock_reason || r.external_message_id || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {excluded.length > 0 && (
        <div className="rounded border border-[#f0d999] bg-[#fef8e5] overflow-hidden">
          <div className="px-2.5 py-1 text-[10.5px] uppercase tracking-[.08em] text-[#8a5a1a] font-semibold">
            Excluded ({excluded.length})
          </div>
          <table className="w-full text-[11.5px]" data-testid="team-review-send-result-excluded-table">
            <tbody>
              {excluded.map((r) => (
                <tr
                  key={`${r.subject_type}:${r.subject_key}`}
                  className="border-t border-[#f0d999]"
                  data-testid={`team-review-send-result-excluded-row-${r.subject_key}`}
                >
                  <td className="py-1 px-2.5 font-semibold text-[#8a5a1a]">{r.subject_name || r.subject_key}</td>
                  <td className="py-1 px-2.5 text-[color:var(--cc-ink-muted)] uppercase tracking-wide text-[10.5px]">
                    {r.subject_type}
                  </td>
                  <td className="py-1 px-2.5 font-mono-data text-[11px] text-[#8a5a1a]">{r.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* pt.91 — CSR Sales Log auto-match summary line on the compute summary card.
   Backend cross-references pending csr_sales entries against this week's ST
   commission export; matched entries auto-flip the CSR-converted flag and
   move into `matched_this_week`. Reversal warnings surface here too. */
function CsrSalesAutoMatchLine({ info, slug }) {
  const auto = info.auto_matched_count || 0;
  const already = (info.already_matched || []).length;
  const reverted = (info.reverted_sales || []).length;
  if (!auto && !already && !reverted) return null;
  return (
    <div className="text-[11.5px]" data-testid="payroll-detail-csr-sales-auto-match">
      <span className="text-[#0e6b52] font-bold">CSR Sales auto-match:</span>{" "}
      <span title={(info.matched_sales || []).map((s) => `${s.job_number} — ${s.csr_name}`).join(", ") || "none new"}>
        {auto} new
      </span>{" · "}
      <span title={(info.already_matched || []).map((s) => `${s.job_number} — ${s.csr_name}`).join(", ") || "none carried"}>
        {already} carried
      </span>
      {reverted > 0 && (
        <>
          {" · "}
          <span className="text-[#7a1c1c] font-bold" title={info.reverted_sales.map((s) => `${s.job_number} — ${s.csr_name}`).join(", ")}>
            ↺ {reverted} reverted (job dropped from export)
          </span>
        </>
      )}
      {" · "}
      <a
        href={`/${slug}/app/payroll/csr-sales`}
        className="text-[#005a94] hover:underline"
        data-testid="payroll-detail-csr-sales-link"
      >
        open log →
      </a>
    </div>
  );
}

